import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, apiCall } from '@/AuthContext';
import { motion } from 'framer-motion';
import { Shield, LogOut, Users, CreditCard, Calendar, UserPlus, BarChart3, MessageSquare, Settings, BookOpen, Search, CheckCircle, Clock, XCircle, Trash2, Printer, Copy, Send, Wallet, FileText, Plus, Download, Trophy, Briefcase, Edit, Target, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getWhatsAppReminderUrl, getBulkReminderData } from '@/utils/whatsapp';
import { downloadReceiptPDF } from '@/utils/receiptPdf';
import jsPDF from 'jspdf';

const EDUCATION_OPTIONS = ['SSLC', 'Plus Two', 'Degree', 'PG', 'Diploma', 'LLB', 'Other Professional Degree'];
const EXPENSE_CATEGORIES = [
  { id: 'rent', label: 'Rent' },
  { id: 'staff_salary', label: 'Staff Salary' },
  { id: 'teachers_salary', label: 'Teachers Salary' },
  { id: 'stationary', label: 'Stationary' },
  { id: 'office_equipment', label: 'Office Equipment' },
  { id: 'asset_purchase', label: 'Asset Purchase' },
  { id: 'website_maintenance', label: 'Website Maintenance' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'other', label: 'Other' },
];
const INCOME_CATEGORIES = [
  { id: 'vishnu', label: 'Vishnu' },
  { id: 'krishna', label: 'Krishna' },
  { id: 'loan', label: 'Loan' },
  { id: 'other', label: 'Other' },
];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [fees, setFees] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showPayFee, setShowPayFee] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBatch, setSelectedBatch] = useState('regular');
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [batchStudents, setBatchStudents] = useState([]);
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const receiptRef = useRef(null);
  const [expenses, setExpenses] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [incomes, setIncomes] = useState([]);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().toLocaleString('en', { month: 'long' }));
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [reportType, setReportType] = useState('monthly');
  const [annualReport, setAnnualReport] = useState(null);
  const [dailyAttendance, setDailyAttendance] = useState(null);
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyBatch, setDailyBatch] = useState('all');
  const [monthlyAttendance, setMonthlyAttendance] = useState(null);
  const [monthlyAttBatch, setMonthlyAttBatch] = useState('all');
  const [waivedFeesReport, setWaivedFeesReport] = useState(null);

  const fetchWaivedFees = async () => {
    try {
      const qs = locationFilter && locationFilter !== 'all' ? `?location=${locationFilter}` : '';
      const r = await apiCall('get', `/reports/waived-fees${qs}`);
      setWaivedFeesReport(r.data);
    } catch (e) { toast.error('Failed to load waived fees'); }
  };
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [cmsPosts, setCmsPosts] = useState([]);
  const [cmsTab, setCmsTab] = useState('current_affairs');
  const [cmsForm, setCmsForm] = useState({ type: 'current_affairs', title: '', content: '', link: '', options: ['', '', '', ''], correct_index: 0, explanation: '' });
  // Quiz state
  const [quizzes, setQuizzes] = useState([]);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [quizForm, setQuizForm] = useState({ date: new Date().toISOString().split('T')[0], title: '', bulk_text: '', day_number: '', positive_marks: 1.0, negative_marks: 0.33 });
  const [editingQuizId, setEditingQuizId] = useState(null);
  const [quizSubTab, setQuizSubTab] = useState('upload'); // upload | student | guest
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  // Notes state
  const [notes, setNotes] = useState([]);
  const [noteForm, setNoteForm] = useState({ title: '', subject: 'General', note_type: 'pdf', pdf_path: '', html_content: '', description: '', batch_id: '', visible_to: 'all', required_tier: 'free', day_number: '' });
  const [noteUploading, setNoteUploading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  // Online subscriptions state
  const [onlineSubs, setOnlineSubs] = useState([]);
  const [onlineSubFilter, setOnlineSubFilter] = useState('payment_submitted');
  const [profitSummary, setProfitSummary] = useState(null);
  const [showWithdrawProfit, setShowWithdrawProfit] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [teachersList, setTeachersList] = useState([]);
  const [showAddStory, setShowAddStory] = useState(false);
  const [showAddTeacher, setShowAddTeacher] = useState(false);

  // Edit/Delete state (Admin CRUD)
  const [showEditStudent, setShowEditStudent] = useState(false);
  const [editStudentForm, setEditStudentForm] = useState({ student_id: '', name: '', phone: '', batch_id: 'regular', monthly_fee: 800, location: 'palachira', email: '', educational_qualification: 'SSLC' });
  const [showEditFee, setShowEditFee] = useState(false);
  const [editFeeForm, setEditFeeForm] = useState({ id: '', amount: 0, status: 'pending', month: '', year: new Date().getFullYear(), type: 'monthly' });
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchForm, setBatchForm] = useState({ id: '', name: '', timing: '', description: '' });
  const [editingBatchId, setEditingBatchId] = useState(null);

  const [locationFilter, setLocationFilter] = useState('all');  // all | palachira | vizhavoor
  const [locations, setLocations] = useState({ active: [], upcoming: [] });

  // Load locations config once
  useEffect(() => {
    apiCall('get', '/locations').then(r => setLocations(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    const qs = locationFilter && locationFilter !== 'all' ? `?location=${locationFilter}` : '';
    const endpoints = [
      ['stats', 'get', `/dashboard/stats${qs}`],
      ['students', 'get', `/students${qs}`],
      ['batches', 'get', '/batches'],
      ['fees', 'get', '/fees'],
      ['contacts', 'get', '/contact'],
      ['users', 'get', '/users'],
      ['enquiries', 'get', '/enquiries'],
      ['expenses', 'get', `/expenses${qs}`],
      ['incomes', 'get', `/other-income${qs}`],
      ['profit', 'get', '/profit/summary'],
      ['withdrawals', 'get', `/profit/withdrawals${qs}`],
      ['stories', 'get', '/success-stories'],
      ['teachers', 'get', '/teachers'],
    ];
    const results = await Promise.allSettled(endpoints.map(([, m, u]) => apiCall(m, u)));
    const setters = {
      stats: setStats, students: setStudents, batches: setBatches, fees: setFees,
      contacts: setContacts, users: setUsers, enquiries: setEnquiries, expenses: setExpenses,
      incomes: setIncomes, profit: setProfitSummary, withdrawals: setWithdrawals,
      stories: setSuccessStories, teachers: setTeachersList,
    };
    const failures = [];
    let statsLoaded = false;
    results.forEach((r, i) => {
      const [key] = endpoints[i];
      if (r.status === 'fulfilled') {
        setters[key](r.value.data);
        if (key === 'stats') statsLoaded = true;
      } else {
        failures.push(key);
        // Set safe defaults so UI doesn't break
        if (['students','batches','fees','contacts','users','enquiries','expenses','incomes','withdrawals','stories','teachers'].includes(key)) {
          setters[key]([]);
        }
      }
    });
    // If stats endpoint failed, set a zero-filled fallback so dashboard still renders
    if (!statsLoaded) {
      setStats({
        total_students: 0, fee_collected: 0, total_other_income: 0, total_income: 0,
        total_pending: 0, total_expenses: 0, net_profit: 0, total_withdrawn: 0,
        net_balance: 0, other_income_by_category: {}, expense_by_category: {},
        income_by_method: { cash: 0, upi: 0, bank_transfer: 0 },
        expense_by_method: { cash: 0, upi: 0, bank_transfer: 0, cheque: 0 },
        balance_by_method: { cash: 0, upi: 0, bank_transfer: 0 },
        total_batches: 0, new_contacts: 0, new_enquiries: 0, batch_counts: {}
      });
      toast.error('Could not load dashboard data — showing zeros. Try Retry button.');
    }
    if (failures.length > 0 && process.env.NODE_ENV === 'development') {
      console.warn('Failed to load:', failures);
    }
  }, [locationFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const filtered = students.filter(s => s.batch_id === selectedBatch && s.status === 'active');
    setBatchStudents(filtered);
    const records = {};
    filtered.forEach(s => { records[s.student_id] = 'present'; });
    setAttendanceRecords(records);
  }, [selectedBatch, students]);

  const filteredStudents = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.student_id?.toLowerCase().includes(search.toLowerCase())
  );

  const tabs = [
    { id: 'overview', label: 'Dashboard', icon: BarChart3 },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'batches', label: 'Batches', icon: BookOpen },
    { id: 'fees', label: 'Fees', icon: CreditCard },
    { id: 'income', label: 'Other Income', icon: Plus },
    { id: 'expenses', label: 'Expenses', icon: Wallet },
    { id: 'profit', label: 'Profit Sharing', icon: Users },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'success', label: 'Success Corner', icon: Trophy },
    { id: 'faculty', label: 'Teachers', icon: Briefcase },
    { id: 'content', label: 'Website Content', icon: FileText },
    { id: 'notes', label: 'Study Notes', icon: BookOpen },
    { id: 'online', label: 'Online Subscriptions', icon: Sparkles },
    { id: 'quiz', label: 'Daily Mock Test', icon: Target },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'enquiries', label: 'Enquiries', icon: MessageSquare },
    { id: 'contacts', label: 'Website Enquiries', icon: MessageSquare },
    { id: 'users', label: 'Staff/Users', icon: Settings },
  ];

  // Registration
  const [regForm, setRegForm] = useState({
    name: '', dob: '', address: '', marital_status: 'single', phone: '',
    educational_qualification: 'SSLC', batch_id: 'regular', monthly_fee: 800, admission_fee: 0, email: '',
    username: '', password: '', location: 'palachira', monthly_fee_waived: false, waiver_reason: ''
  });

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...regForm };
      // If admission fee waiver checkbox on, admission_fee already set to 0 in UI
      const res = await apiCall('post', '/students/register', payload);
      toast.success(`Student registered at ${res.data.location || regForm.location}! ID: ${res.data.student_id}`);
      setShowRegister(false);
      setRegForm({ name: '', dob: '', address: '', marital_status: 'single', phone: '', educational_qualification: 'SSLC', batch_id: 'regular', monthly_fee: 800, admission_fee: 0, email: '', username: '', password: '', location: 'palachira', monthly_fee_waived: false, waiver_reason: '' });
      setCredentials({ username: res.data.username, password: res.data.password, student_id: res.data.student_id, name: res.data.name, login_email: res.data.login_email });
      setShowCredentials(true);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    }
  };

  // User create
  const [userForm, setUserForm] = useState({ email: '', password: '', name: '', role: 'staff' });
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await apiCall('post', '/users/create', userForm);
      toast.success('User created');
      setShowCreateUser(false);
      setUserForm({ email: '', password: '', name: '', role: 'staff' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await apiCall('delete', `/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  // Fee payment
  const [payForm, setPayForm] = useState({ student_id: '', month: '', year: new Date().getFullYear(), amount: 0, payment_method: 'cash' });
  const [payMode, setPayMode] = useState('full'); // full | partial | waive
  const [partialAmount, setPartialAmount] = useState(0);
  const [waiveReason, setWaiveReason] = useState('');

  const openPayFee = (studentId) => {
    const studentFees = fees.filter(f => f.student_id === studentId && ['pending', 'partial'].includes(f.status));
    setSelectedStudent(studentId);
    if (studentFees.length > 0) {
      const f = studentFees[0];
      const balance = Math.max(0, (f.amount || 0) - (f.amount_paid || 0));
      setPayForm({ student_id: studentId, month: f.month, year: f.year, amount: f.amount, payment_method: 'cash' });
      setPartialAmount(balance);
      setPayMode(f.status === 'partial' ? 'partial' : 'full');
    }
    setWaiveReason('');
    setShowPayFee(true);
  };

  const handlePayFee = async (e) => {
    e.preventDefault();
    try {
      if (payMode === 'waive') {
        await apiCall('post', '/fees/waive', { student_id: payForm.student_id, month: payForm.month, year: payForm.year, reason: waiveReason });
        toast.success('Fee waived off');
      } else if (payMode === 'partial') {
        const amt = parseFloat(partialAmount) || 0;
        if (amt <= 0) { toast.error('Enter a valid partial amount'); return; }
        await apiCall('post', '/fees/partial-pay', { student_id: payForm.student_id, month: payForm.month, year: payForm.year, amount: amt, payment_method: payForm.payment_method, notes: '' });
        toast.success(`Partial ₹${amt} recorded`);
      } else {
        await apiCall('post', '/fees/pay', payForm);
        toast.success('Payment recorded (Full)');
      }
      setShowPayFee(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleMonthlyWaiver = async (s) => {
    // Deprecated — kept for compat. Use per-fee waive instead.
    toast.info('Please waive each month individually from Fees tab → Collect → Waive Off');
  };

  const dismissStudent = async (s) => {
    const reason = window.prompt(`Dismiss ${s.name}?\nThis will mark the student as dismissed. No fees will be generated. Record retained for audit.\n\nReason:`, '');
    if (reason === null) return;
    try {
      const res = await apiCall('post', `/students/${s.student_id}/dismiss`, { reason });
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteFinanceRecord = async (type, id, label) => {
    if (!window.confirm(`Delete this ${label}? This cannot be undone.`)) return;
    try {
      const path = type === 'withdrawal' ? `/profit/withdrawals/${id}` : `/${type}/${id}`;
      await apiCall('delete', path);
      toast.success(`${label} deleted`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // === Admin CRUD handlers ===
  const openEditStudent = (s) => {
    setEditStudentForm({
      student_id: s.student_id,
      name: s.name || '',
      phone: s.phone || '',
      batch_id: s.batch_id || 'regular',
      monthly_fee: s.monthly_fee || 800,
      location: s.location || 'palachira',
      email: s.email || '',
      educational_qualification: s.educational_qualification || 'SSLC',
      address: s.address || '',
    });
    setShowEditStudent(true);
  };
  const handleEditStudent = async (e) => {
    e.preventDefault();
    const { student_id, ...payload } = editStudentForm;
    payload.monthly_fee = parseInt(payload.monthly_fee) || 800;
    try {
      await apiCall('put', `/students/${student_id}`, payload);
      toast.success('Student updated');
      setShowEditStudent(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };
  const handleDeleteStudent = async (s) => {
    if (!window.confirm(`Permanently DELETE ${s.name} (${s.student_id})?\nAll fees, attendance and login will be removed. This cannot be undone.`)) return;
    try {
      await apiCall('delete', `/students/${s.student_id}`);
      toast.success('Student deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEditFee = (f) => {
    setEditFeeForm({
      id: f._id || f.id,
      amount: f.amount || 0,
      status: f.status || 'pending',
      month: f.month || '',
      year: f.year || new Date().getFullYear(),
      type: f.type || 'monthly',
      amount_paid: f.amount_paid || 0,
    });
    setShowEditFee(true);
  };
  const handleEditFee = async (e) => {
    e.preventDefault();
    const { id, ...payload } = editFeeForm;
    payload.amount = parseFloat(payload.amount) || 0;
    payload.amount_paid = parseFloat(payload.amount_paid) || 0;
    payload.year = parseInt(payload.year) || new Date().getFullYear();
    try {
      await apiCall('put', `/fees/${id}`, payload);
      toast.success('Fee record updated');
      setShowEditFee(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteFee = async (f) => {
    if (!window.confirm(`Delete this fee record (${f.month} ${f.year} · ₹${f.amount})?`)) return;
    try {
      await apiCall('delete', `/fees/${f._id || f.id}`);
      toast.success('Fee record deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openCreateBatch = () => {
    setEditingBatchId(null);
    setBatchForm({ id: '', name: '', timing: '', description: '' });
    setShowBatchDialog(true);
  };
  const openEditBatch = (b) => {
    setEditingBatchId(b.id);
    setBatchForm({ id: b.id, name: b.name || '', timing: b.timing || '', description: b.description || '' });
    setShowBatchDialog(true);
  };
  const handleSaveBatch = async (e) => {
    e.preventDefault();
    try {
      if (editingBatchId) {
        await apiCall('put', `/batches/${editingBatchId}`, { name: batchForm.name, timing: batchForm.timing, description: batchForm.description });
        toast.success('Batch updated');
      } else {
        await apiCall('post', '/batches', batchForm);
        toast.success('Batch created');
      }
      setShowBatchDialog(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteBatch = async (b) => {
    if (!window.confirm(`Delete batch "${b.name}"?`)) return;
    try {
      await apiCall('delete', `/batches/${b.id}`);
      toast.success('Batch deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const viewReceipt = async (studentId, month, year) => {
    try {
      const res = await apiCall('get', `/fees/receipt/${studentId}/${month}/${year}`);
      setReceipt(res.data);
      setShowReceipt(true);
    } catch { toast.error('Receipt not available'); }
  };

  const handleDownloadPDF = () => {
    if (receipt) { downloadReceiptPDF(receipt); toast.success('Receipt PDF downloaded'); }
  };

  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', email: '', course_interest: '', notes: '', source: 'walk-in' });
  const handleAddEnquiry = async (e) => {
    e.preventDefault();
    try {
      await apiCall('post', '/enquiries', enquiryForm);
      toast.success('Enquiry added');
      setShowEnquiry(false);
      setEnquiryForm({ name: '', phone: '', email: '', course_interest: '', notes: '', source: 'walk-in' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const [showBulkReminder, setShowBulkReminder] = useState(false);

  // Profit withdrawal
  const [profitForm, setProfitForm] = useState({
    month: new Date().toLocaleString('en', { month: 'long' }),
    year: new Date().getFullYear(),
    partner1_name: 'Partner 1', partner1_amount: '',
    partner2_name: 'Partner 2', partner2_amount: '',
    notes: '', date: new Date().toISOString().split('T')[0], payment_method: 'cash'
  });

  const handleWithdrawProfit = async (e) => {
    e.preventDefault();
    try {
      await apiCall('post', '/profit/withdraw', {
        ...profitForm,
        partner1_amount: parseFloat(profitForm.partner1_amount),
        partner2_amount: parseFloat(profitForm.partner2_amount)
      });
      toast.success('Profit withdrawal recorded');
      setShowWithdrawProfit(false);
      setProfitForm({ ...profitForm, partner1_amount: '', partner2_amount: '', notes: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const splitEqually = () => {
    if (!profitSummary) return;
    const half = Math.floor(profitSummary.net_profit / 2);
    setProfitForm({ ...profitForm, partner1_amount: String(half), partner2_amount: String(half) });
  };

  // Expense form
  const [expenseForm, setExpenseForm] = useState({ category: 'rent', amount: '', description: '', date: new Date().toISOString().split('T')[0], payment_method: 'cash' });
  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await apiCall('post', '/expenses', { ...expenseForm, amount: parseFloat(expenseForm.amount) });
      toast.success('Expense added');
      setShowAddExpense(false);
      setExpenseForm({ category: 'rent', amount: '', description: '', date: new Date().toISOString().split('T')[0], payment_method: 'cash' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Other Income form
  const [incomeForm, setIncomeForm] = useState({ category: 'vishnu', amount: '', description: '', date: new Date().toISOString().split('T')[0], payment_method: 'cash' });
  const handleAddIncome = async (e) => {
    e.preventDefault();
    try {
      await apiCall('post', '/other-income', { ...incomeForm, amount: parseFloat(incomeForm.amount) });
      toast.success('Income added');
      setShowAddIncome(false);
      setIncomeForm({ category: 'vishnu', amount: '', description: '', date: new Date().toISOString().split('T')[0], payment_method: 'cash' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Monthly Report
  const fetchReport = async () => {
    try {
      const res = await apiCall('get', `/reports/monthly?month=${reportMonth}&year=${reportYear}`);
      setReport(res.data);
    } catch { toast.error('Failed to load report'); }
  };

  // Annual Report
  const fetchAnnualReport = async () => {
    try {
      const res = await apiCall('get', `/reports/annual?year=${reportYear}`);
      setAnnualReport(res.data);
    } catch { toast.error('Failed to load annual report'); }
  };

  // CMS Posts
  const fetchCmsPosts = async () => {
    try { const res = await apiCall('get', '/cms/posts'); setCmsPosts(res.data); } catch {}
  };
  useEffect(() => { fetchCmsPosts(); }, []);

  const handleAddCmsPost = async (e) => {
    e.preventDefault();
    try {
      const payload = { type: cmsTab, title: cmsForm.title, content: cmsForm.content, link: cmsForm.link };
      if (cmsTab === 'mock_question') {
        payload.options = cmsForm.options.filter(o => o.trim());
        payload.correct_index = parseInt(cmsForm.correct_index);
        payload.explanation = cmsForm.explanation;
      }
      await apiCall('post', '/cms/posts', payload);
      toast.success('Posted to website');
      setCmsForm({ type: cmsTab, title: '', content: '', link: '', options: ['', '', '', ''], correct_index: 0, explanation: '' });
      fetchCmsPosts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteCmsPost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try { await apiCall('delete', `/cms/posts/${id}`); toast.success('Deleted'); fetchCmsPosts(); } catch { toast.error('Failed'); }
  };

  // Quiz handlers
  const fetchQuizzes = async () => {
    try {
      const [qz, at] = await Promise.all([apiCall('get', '/quizzes'), apiCall('get', '/quiz-attempts')]);
      setQuizzes(qz.data); setQuizAttempts(at.data);
    } catch {}
  };
  useEffect(() => { fetchQuizzes(); }, []);

  // Bulk parser supporting:
  // 1. Standard MCQ:           "1. Question?  A) opt B) opt*  E: explain"
  // 2. Statement-type MCQ:     "1. Consider the following statements:
  //                             S1) Statement one
  //                             S2) Statement two
  //                             Which is correct?
  //                             A) Only 1
  //                             B) Only 2*
  //                             C) Both
  //                             D) Neither
  //                             E: explanation"
  // - Any line after question marker that ISN'T S#/A-D/E gets appended to question text
  // - S#) lines become numbered statements within the question
  const parseBulkQuestions = (text) => {
    const blocks = text.split(/^\s*---\s*$/m).map(b => b.trim()).filter(Boolean);
    const result = [];
    for (const block of blocks) {
      const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
      let questionLines = [], options = [], correct_index = -1, explanation = '', subject = '';
      let firstLineSet = false;
      for (const line of lines) {
        const qm = line.match(/^(?:Q[:.\)]|\d+[\.\)])\s*(.+)$/i);
        const sm = line.match(/^S(\d+)[\)\.]\s*(.+)$/i);
        const om = line.match(/^([A-D])[\)\.]\s*(.+)$/i);
        const em = line.match(/^E[:.\)]\s*(.+)$/i);
        const subjMatch = line.match(/^Subject\s*[:\-]\s*(.+)$/i);
        if (subjMatch) {
          subject = subjMatch[1].trim();
        } else if (om) {
          let val = om[2].trim();
          if (val.endsWith('*')) { correct_index = options.length; val = val.slice(0, -1).trim(); }
          options.push(val);
        } else if (em) {
          explanation = em[1].trim();
        } else if (sm) {
          questionLines.push(`${sm[1]}. ${sm[2].trim()}`);
        } else if (qm && !firstLineSet) {
          questionLines.push(qm[1].trim());
          firstLineSet = true;
        } else if (firstLineSet) {
          questionLines.push(line);
        }
      }
      const question = questionLines.join('\n').trim();
      if (question && options.length >= 2 && correct_index >= 0) {
        result.push({ question, options, correct_index, explanation, subject });
      }
    }
    return result;
  };

  // Serialize parsed questions back into bulk text (for editing)
  const serializeBulkQuestions = (questions) => {
    return questions.map((q, idx) => {
      const lines = [];
      if (q.subject) lines.push(`Subject: ${q.subject}`);
      const qLines = (q.question || '').split('\n');
      lines.push(`${idx + 1}. ${qLines[0]}`);
      for (let i = 1; i < qLines.length; i++) lines.push(qLines[i]);
      const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
      (q.options || []).forEach((opt, i) => {
        const mark = i === q.correct_index ? '*' : '';
        lines.push(`${letters[i]}) ${opt}${mark}`);
      });
      if (q.explanation) lines.push(`E: ${q.explanation}`);
      return lines.join('\n');
    }).join('\n---\n');
  };

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    const parsed = parseBulkQuestions(quizForm.bulk_text);
    if (parsed.length === 0) { toast.error('No valid questions parsed. Check format.'); return; }
    const payload = {
      date: quizForm.date,
      title: quizForm.title,
      questions: parsed,
      day_number: quizForm.day_number ? parseInt(quizForm.day_number) : null,
      positive_marks: parseFloat(quizForm.positive_marks) || 1.0,
      negative_marks: parseFloat(quizForm.negative_marks) || 0,
    };
    try {
      if (editingQuizId) {
        const res = await apiCall('put', `/quizzes/${editingQuizId}`, payload);
        toast.success(`Quiz updated (${res.data.questions_count} questions)`);
      } else {
        const res = await apiCall('post', '/quizzes', payload);
        toast.success(`Quiz created with ${res.data.questions_count} questions`);
      }
      setQuizForm({ date: new Date().toISOString().split('T')[0], title: '', bulk_text: '', day_number: '', positive_marks: 1.0, negative_marks: 0.33 });
      setEditingQuizId(null);
      fetchQuizzes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const editQuiz = (q) => {
    setEditingQuizId(q.id);
    setQuizForm({
      date: q.date,
      title: q.title || '',
      bulk_text: serializeBulkQuestions(q.questions || []),
      day_number: q.day_number ? String(q.day_number) : '',
      positive_marks: q.positive_marks ?? 1.0,
      negative_marks: q.negative_marks ?? 0.33,
    });
    setQuizSubTab('upload');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelQuizEdit = () => {
    setEditingQuizId(null);
    setQuizForm({ date: new Date().toISOString().split('T')[0], title: '', bulk_text: '', day_number: '', positive_marks: 1.0, negative_marks: 0.33 });
  };

  const deleteQuiz = async (id) => {
    if (!window.confirm('Delete this quiz and all its attempts?')) return;
    try { await apiCall('delete', `/quizzes/${id}`); toast.success('Deleted'); fetchQuizzes(); } catch { toast.error('Failed'); }
  };

  // Notes handlers
  const fetchNotes = async () => {
    try { const r = await apiCall('get', '/notes'); setNotes(r.data); } catch {}
  };
  useEffect(() => { fetchNotes(); }, []);

  const handleNotePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files allowed'); return; }
    setNoteUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await apiCall('post', '/upload-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNoteForm(f => ({ ...f, pdf_path: res.data.path }));
      toast.success('PDF uploaded');
    } catch (er) { toast.error(er.response?.data?.detail || 'Upload failed'); }
    setNoteUploading(false);
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    const payload = {
      ...noteForm,
      day_number: noteForm.day_number ? parseInt(noteForm.day_number) : null,
    };
    try {
      if (editingNoteId) {
        await apiCall('put', `/notes/${editingNoteId}`, payload);
        toast.success('Note updated');
      } else {
        await apiCall('post', '/notes', payload);
        toast.success('Note published');
      }
      setNoteForm({ title: '', subject: 'General', note_type: 'pdf', pdf_path: '', html_content: '', description: '', batch_id: '', visible_to: 'all', required_tier: 'free', day_number: '' });
      setEditingNoteId(null);
      fetchNotes();
    } catch (er) { toast.error(er.response?.data?.detail || 'Failed'); }
  };

  const editNote = (n) => {
    setEditingNoteId(n.id);
    setNoteForm({
      title: n.title || '',
      subject: n.subject || 'General',
      note_type: n.note_type || 'pdf',
      pdf_path: n.pdf_path || '',
      html_content: n.html_content || '',
      description: n.description || '',
      batch_id: n.batch_id || '',
      visible_to: n.visible_to || 'all',
      required_tier: n.required_tier || 'free',
      day_number: n.day_number ? String(n.day_number) : '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelNoteEdit = () => {
    setEditingNoteId(null);
    setNoteForm({ title: '', subject: 'General', note_type: 'pdf', pdf_path: '', html_content: '', description: '', batch_id: '', visible_to: 'all', required_tier: 'free', day_number: '' });
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try { await apiCall('delete', `/notes/${id}`); toast.success('Deleted'); fetchNotes(); } catch { toast.error('Failed'); }
  };

  // Online subscriptions
  const fetchOnlineSubs = async () => {
    try { const r = await apiCall('get', '/online/subscriptions'); setOnlineSubs(r.data); } catch {}
  };
  useEffect(() => { fetchOnlineSubs(); }, []);
  const approveSub = async (id) => {
    try { const r = await apiCall('post', `/online/approve/${id}`); toast.success(r.data.message); fetchOnlineSubs(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const rejectSub = async (id) => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    try { await apiCall('post', `/online/reject/${id}?reason=${encodeURIComponent(reason)}`); toast.success('Rejected'); fetchOnlineSubs(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Daily Attendance
  const fetchDailyAttendance = async () => {
    try {
      const batchParam = dailyBatch === 'all' ? '' : `&batch_id=${dailyBatch}`;
      const res = await apiCall('get', `/reports/attendance/daily?date=${dailyDate}${batchParam}`);
      setDailyAttendance(res.data);
    } catch { toast.error('Failed to load attendance'); }
  };

  // Monthly Per-Student Attendance
  const fetchMonthlyAttendance = async () => {
    try {
      const batchParam = monthlyAttBatch === 'all' ? '' : `&batch_id=${monthlyAttBatch}`;
      const res = await apiCall('get', `/reports/attendance/monthly?month=${reportMonth}&year=${reportYear}${batchParam}`);
      setMonthlyAttendance(res.data);
      setExpandedStudent(null);
    } catch { toast.error('Failed to load monthly attendance'); }
  };

  const downloadMonthlyAttendancePDF = () => {
    if (!monthlyAttendance) return;
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const m = 10;
    let y = 15;
    const ensureSpace = (n) => { if (y + n > ph - 10) { doc.addPage(); y = 15; } };

    doc.setFontSize(15); doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURE PSC COACHING CENTRE', pw / 2, y, { align: 'center' }); y += 5;
    doc.setFontSize(8); doc.setFont('helvetica', 'italic');
    doc.text('A unit of Narzis Ventures LLP', pw / 2, y, { align: 'center' }); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text('Palachira, Varkala | Phone: 8281583404', pw / 2, y, { align: 'center' }); y += 5;
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.6); doc.line(m, y, pw - m, y); y += 6;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text(`Monthly Attendance Report - ${monthlyAttendance.month} ${monthlyAttendance.year}`, pw / 2, y, { align: 'center' }); y += 7;
    doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(180, 130, 0);
    doc.text(`Total Classes Held: ${monthlyAttendance.class_days_held}  |  Calendar Days: ${monthlyAttendance.days_in_month}`, pw / 2, y, { align: 'center' });
    doc.setTextColor(20, 20, 20); y += 8;

    // Summary
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`Students: ${monthlyAttendance.totals.students}  |  Present: ${monthlyAttendance.totals.present}  |  Late: ${monthlyAttendance.totals.late}  |  Leave: ${monthlyAttendance.totals.leave}  |  Absent: ${monthlyAttendance.totals.absent}`, m, y);
    y += 7;

    // Summary table header
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('#', m, y);
    doc.text('Student Name', m + 6, y);
    doc.text('Batch', m + 60, y);
    doc.text('P', m + 90, y);
    doc.text('L', m + 100, y);
    doc.text('Le', m + 110, y);
    doc.text('A', m + 120, y);
    doc.text('Att', m + 130, y);
    doc.text('Att%', m + 148, y);
    doc.text('Tests', m + 168, y);
    doc.text('Marks', m + 185, y);
    doc.text('Best%', pw - m - 2, y, { align: 'right' });
    y += 2; doc.line(m, y, pw - m, y); y += 4;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);

    monthlyAttendance.students.forEach((s, idx) => {
      ensureSpace(5);
      doc.text(String(idx + 1), m, y);
      doc.text(String(s.student_name || '').slice(0, 24), m + 6, y);
      doc.text(String(s.batch_id || '-').slice(0, 10), m + 60, y);
      doc.text(String(s.present), m + 90, y);
      doc.text(String(s.late), m + 100, y);
      doc.text(String(s.leave), m + 110, y);
      doc.text(String(s.absent), m + 120, y);
      doc.text(`${s.present + s.late}/${s.class_days_held}`, m + 130, y);
      doc.text(`${s.percentage}%`, m + 148, y);
      doc.text(String(s.tests_taken || 0), m + 168, y);
      doc.text(`${s.total_marks || 0}${s.total_possible ? `/${s.total_possible}` : ''}`, m + 185, y);
      doc.text(`${s.best_test_pct || 0}%`, pw - m - 2, y, { align: 'right' });
      y += 4.5;
    });

    // ===== Per-Day Class Totals =====
    if (monthlyAttendance.day_totals?.length > 0) {
      ensureSpace(20);
      y += 6;
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
      doc.text(`Class-Day Totals (${monthlyAttendance.day_totals.length} classes)`, m, y); y += 6;
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('Date', m, y);
      doc.text('Day', m + 35, y);
      doc.text('Present', m + 60, y);
      doc.text('Late', m + 90, y);
      doc.text('Leave', m + 115, y);
      doc.text('Absent', m + 140, y);
      doc.text('Total Marked', m + 170, y);
      y += 2; doc.line(m, y, pw - m, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      monthlyAttendance.day_totals.forEach(d => {
        ensureSpace(5);
        const dayName = new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short' });
        doc.text(d.date, m, y);
        doc.text(dayName, m + 35, y);
        doc.text(String(d.present), m + 60, y);
        doc.text(String(d.late), m + 90, y);
        doc.text(String(d.leave), m + 115, y);
        doc.text(String(d.absent), m + 140, y);
        doc.text(String(d.total_marked), m + 170, y);
        y += 4.5;
      });
    }

    // Day-by-day grid per student (new page)
    monthlyAttendance.students.forEach(s => {
      doc.addPage();
      y = 15;
      doc.setFontSize(12); doc.setFont('helvetica', 'bold');
      doc.text(`${s.student_name} (${s.student_id}) - ${monthlyAttendance.month} ${monthlyAttendance.year}`, m, y); y += 5;
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.text(`Phone: ${s.phone || '-'} | Batch: ${s.batch_id || '-'} | P:${s.present} L:${s.late} Le:${s.leave} A:${s.absent} | Attended ${s.present + s.late}/${s.class_days_held} = ${s.percentage}%`, m, y);
      y += 7;
      // Grid: 7 cols (week-like), label code per day
      const cellW = 12, cellH = 9;
      const codeMap = { present: 'P', late: 'L', leave: 'Le', absent: 'A', '': '-' };
      const colorMap = { present: [16, 185, 129], late: [245, 158, 11], leave: [139, 92, 246], absent: [239, 68, 68], '': [200, 200, 200] };
      doc.setFontSize(8);
      s.days.forEach((d, i) => {
        const col = i % 7;
        const row = Math.floor(i / 7);
        const x = m + col * cellW;
        const yy = y + row * cellH;
        const [r, g, b] = colorMap[d.status] || colorMap[''];
        doc.setFillColor(r, g, b); doc.rect(x, yy, cellW - 1, cellH - 1, 'F');
        doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold');
        doc.text(String(d.day), x + 1, yy + 3);
        doc.setFont('helvetica', 'normal');
        doc.text(codeMap[d.status] || '-', x + 1, yy + 7);
      });
      doc.setTextColor(20, 20, 20);
    });

    doc.setFontSize(7); doc.setFont('helvetica', 'italic'); doc.setTextColor(120, 120, 120);
    doc.text(`Generated on ${new Date().toLocaleString()} - SIGNATURE PSC COACHING CENTRE`, pw / 2, ph - 5, { align: 'center' });
    doc.save(`Attendance_${monthlyAttendance.month}_${monthlyAttendance.year}.pdf`);
    toast.success('Attendance report downloaded');
  };

  const downloadReportPDF = () => {
    if (!report) return;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const m = 15;
    let y = 18;

    const ensureSpace = (needed) => {
      if (y + needed > ph - 15) { doc.addPage(); y = 18; }
    };

    // Header
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURE PSC COACHING CENTRE', pw / 2, y, { align: 'center' }); y += 5;
    doc.setFontSize(8); doc.setFont('helvetica', 'italic');
    doc.text('A unit of Narzis Ventures LLP', pw / 2, y, { align: 'center' }); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text('Palachira, Varkala | Phone: 8281583404', pw / 2, y, { align: 'center' }); y += 6;
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.8); doc.line(m, y, pw - m, y); y += 7;
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text(`Monthly Report - ${report.month} ${report.year}`, pw / 2, y, { align: 'center' }); y += 10;

    // ===== SUMMARY =====
    doc.setFillColor(245, 245, 245); doc.rect(m, y - 4, pw - 2 * m, 32, 'F');
    doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
    doc.text('SUMMARY', m + 2, y); y += 6;
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    const sumRows = [
      ['Fee Collected (Monthly)', `Rs. ${report.income.monthly_collected}`],
      ['Admission Collected', `Rs. ${report.income.admission_collected}`],
      ['Other Income', `Rs. ${report.income.other_income || 0}`],
      ['Total Income', `Rs. ${report.income.total_income}`],
      ['Total Expenses', `Rs. ${report.expenses.total}`],
      ['Net Profit', `Rs. ${report.net_profit}`],
    ];
    sumRows.forEach(([l, v]) => { doc.text(l, m + 4, y); doc.text(v, pw - m - 2, y, { align: 'right' }); y += 4.5; });
    y += 6;

    // ===== ITEM-BY-ITEM: PAID FEES =====
    if (report.paid_fees?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(5, 105, 70);
      doc.text(`PAID FEES (Item by Item) - ${report.paid_fees.length} entries`, m, y); y += 6;
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('#', m, y); doc.text('Student', m + 8, y); doc.text('Type', m + 70, y);
      doc.text('Method', m + 95, y); doc.text('Date', m + 120, y); doc.text('Amount', pw - m - 2, y, { align: 'right' });
      y += 2; doc.setDrawColor(180, 180, 180); doc.line(m, y, pw - m, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      report.paid_fees.forEach((f, idx) => {
        ensureSpace(5);
        doc.text(String(idx + 1), m, y);
        doc.text(String(f.student_name || '').slice(0, 30), m + 8, y);
        doc.text(String(f.type || ''), m + 70, y);
        doc.text(String(f.payment_method || '-'), m + 95, y);
        doc.text(String((f.paid_date || '').split('T')[0]), m + 120, y);
        doc.text(`Rs. ${f.amount}`, pw - m - 2, y, { align: 'right' });
        y += 4.5;
      });
      doc.setFont('helvetica', 'bold');
      doc.text(`Subtotal Fees: Rs. ${report.income.monthly_collected + report.income.admission_collected}`, pw - m - 2, y, { align: 'right' });
      y += 8;
    }

    // ===== ITEM-BY-ITEM: OTHER INCOME =====
    if (report.other_incomes?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(5, 105, 70);
      doc.text(`OTHER INCOME (Item by Item) - ${report.other_incomes.length} entries`, m, y); y += 6;
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('#', m, y); doc.text('Source', m + 8, y); doc.text('Description', m + 50, y);
      doc.text('Method', m + 110, y); doc.text('Date', m + 135, y); doc.text('Amount', pw - m - 2, y, { align: 'right' });
      y += 2; doc.line(m, y, pw - m, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      report.other_incomes.forEach((i, idx) => {
        ensureSpace(5);
        doc.text(String(idx + 1), m, y);
        doc.text(String(i.category || '').slice(0, 20), m + 8, y);
        doc.text(String(i.description || '-').slice(0, 28), m + 50, y);
        doc.text(String(i.payment_method || '-'), m + 110, y);
        doc.text(String(i.date || ''), m + 135, y);
        doc.text(`Rs. ${i.amount}`, pw - m - 2, y, { align: 'right' });
        y += 4.5;
      });
      doc.setFont('helvetica', 'bold');
      doc.text(`Subtotal Other Income: Rs. ${report.income.other_income || 0}`, pw - m - 2, y, { align: 'right' });
      y += 8;
    }

    // ===== ITEM-BY-ITEM: EXPENSES =====
    if (report.expenses.details?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(180, 80, 0);
      doc.text(`EXPENSES (Item by Item) - ${report.expenses.details.length} entries`, m, y); y += 6;
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('#', m, y); doc.text('Category', m + 8, y); doc.text('Description', m + 50, y);
      doc.text('Method', m + 110, y); doc.text('Date', m + 135, y); doc.text('Amount', pw - m - 2, y, { align: 'right' });
      y += 2; doc.line(m, y, pw - m, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      report.expenses.details.forEach((e, idx) => {
        ensureSpace(5);
        doc.text(String(idx + 1), m, y);
        doc.text(String(e.category || '').replace(/_/g, ' ').slice(0, 20), m + 8, y);
        doc.text(String(e.description || '-').slice(0, 28), m + 50, y);
        doc.text(String(e.payment_method || '-'), m + 110, y);
        doc.text(String(e.date || ''), m + 135, y);
        doc.text(`Rs. ${e.amount}`, pw - m - 2, y, { align: 'right' });
        y += 4.5;
      });
      doc.setFont('helvetica', 'bold');
      doc.text(`Subtotal Expenses: Rs. ${report.expenses.total}`, pw - m - 2, y, { align: 'right' });
      y += 8;
    }

    // ===== PENDING FEES =====
    if (report.pending_fees?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(180, 30, 30);
      doc.text(`PENDING FEES - ${report.pending_fees.length} students`, m, y); y += 6;
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('#', m, y); doc.text('Student', m + 8, y); doc.text('Phone', m + 70, y);
      doc.text('Type', m + 110, y); doc.text('Amount', pw - m - 2, y, { align: 'right' });
      y += 2; doc.line(m, y, pw - m, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      report.pending_fees.forEach((f, idx) => {
        ensureSpace(5);
        doc.text(String(idx + 1), m, y);
        doc.text(String(f.student_name || '').slice(0, 28), m + 8, y);
        doc.text(String(f.phone || '-'), m + 70, y);
        doc.text(String(f.type || ''), m + 110, y);
        doc.text(`Rs. ${f.amount}`, pw - m - 2, y, { align: 'right' });
        y += 4.5;
      });
      y += 6;
    }

    // ===== NET / FINAL =====
    ensureSpace(20);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.6); doc.line(m, y, pw - m, y); y += 6;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 20, 20);
    doc.text('NET PROFIT', m, y); doc.text(`Rs. ${report.net_profit}`, pw - m - 2, y, { align: 'right' }); y += 8;

    // Attendance summary
    const att = report.attendance;
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text('Attendance:', m, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`Present ${att.present} | Late ${att.late} | Leave ${att.leave} | Absent ${att.absent} | Total ${att.total}`, m + 25, y);
    y += 8;

    doc.setFontSize(7); doc.setFont('helvetica', 'italic'); doc.setTextColor(120, 120, 120);
    doc.text(`Generated on ${new Date().toLocaleString()} - SIGNATURE PSC COACHING CENTRE`, pw / 2, ph - 8, { align: 'center' });

    doc.save(`Report_${report.month}_${report.year}.pdf`);
    toast.success('Detailed report PDF downloaded');
  };

  const downloadAnnualPDF = () => {
    if (!annualReport) return;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const m = 15;
    let y = 18;
    const ensureSpace = (needed) => { if (y + needed > ph - 15) { doc.addPage(); y = 18; } };

    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURE PSC COACHING CENTRE', pw / 2, y, { align: 'center' }); y += 5;
    doc.setFontSize(8); doc.setFont('helvetica', 'italic');
    doc.text('A unit of Narzis Ventures LLP', pw / 2, y, { align: 'center' }); y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text('Palachira, Varkala | Phone: 8281583404', pw / 2, y, { align: 'center' }); y += 6;
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.8); doc.line(m, y, pw - m, y); y += 7;
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text(`Annual Report - ${annualReport.year}`, pw / 2, y, { align: 'center' }); y += 10;

    // Summary
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text('ANNUAL SUMMARY', m, y); y += 6;
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    [
      ['Total Fee Collected', `Rs. ${annualReport.income.total_collected}`],
      ['Total Other Income', `Rs. ${annualReport.income.total_other_income || 0}`],
      ['Total Income', `Rs. ${annualReport.income.total_income || annualReport.income.total_collected}`],
      ['Total Pending', `Rs. ${annualReport.income.total_pending}`],
      ['Total Expenses', `Rs. ${annualReport.expenses.total}`],
      ['Net Profit', `Rs. ${annualReport.net_profit}`],
      ['Total Withdrawn', `Rs. ${annualReport.total_withdrawn}`],
      ['Balance', `Rs. ${annualReport.balance}`],
    ].forEach(([l, v]) => { doc.text(l, m + 4, y); doc.text(v, pw - m - 2, y, { align: 'right' }); y += 4.5; });
    y += 6;

    // Monthly breakdown
    if (annualReport.monthly_breakdown?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.text('MONTH-BY-MONTH BREAKDOWN', m, y); y += 6;
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('Month', m, y);
      doc.text('Fees', m + 30, y);
      doc.text('Other Inc.', m + 55, y);
      doc.text('Pending', m + 85, y);
      doc.text('Expenses', m + 115, y);
      doc.text('Net', pw - m - 2, y, { align: 'right' });
      y += 2; doc.line(m, y, pw - m, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      annualReport.monthly_breakdown.forEach(mb => {
        ensureSpace(5);
        doc.text(mb.month, m, y);
        doc.text(`Rs. ${mb.collected}`, m + 30, y);
        doc.text(`Rs. ${mb.other_income || 0}`, m + 55, y);
        doc.text(`Rs. ${mb.pending}`, m + 85, y);
        doc.text(`Rs. ${mb.expenses}`, m + 115, y);
        doc.text(`Rs. ${mb.net}`, pw - m - 2, y, { align: 'right' });
        y += 4.5;
      });
      y += 6;
    }

    // Pending Fees list
    if (annualReport.pending_fees?.length > 0) {
      ensureSpace(20);
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(180, 30, 30);
      doc.text(`ALL PENDING FEES - ${annualReport.pending_fees.length} records`, m, y); y += 6;
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('#', m, y); doc.text('Student', m + 8, y); doc.text('Month', m + 60, y);
      doc.text('Type', m + 85, y); doc.text('Phone', m + 110, y); doc.text('Amount', pw - m - 2, y, { align: 'right' });
      y += 2; doc.line(m, y, pw - m, y); y += 4;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(20, 20, 20);
      annualReport.pending_fees.forEach((f, idx) => {
        ensureSpace(5);
        doc.text(String(idx + 1), m, y);
        doc.text(String(f.student_name || '').slice(0, 25), m + 8, y);
        doc.text(String(f.month || '').slice(0, 10), m + 60, y);
        doc.text(String(f.type || ''), m + 85, y);
        doc.text(String(f.phone || '-'), m + 110, y);
        doc.text(`Rs. ${f.amount}`, pw - m - 2, y, { align: 'right' });
        y += 4.5;
      });
    }

    doc.setFontSize(7); doc.setFont('helvetica', 'italic'); doc.setTextColor(120, 120, 120);
    doc.text(`Generated on ${new Date().toLocaleString()} - SIGNATURE PSC COACHING CENTRE`, pw / 2, ph - 8, { align: 'center' });

    doc.save(`Annual_Report_${annualReport.year}.pdf`);
    toast.success('Annual report PDF downloaded');
  };

  const sendWhatsAppReminder = (fee) => {
    const student = students.find(s => s.student_id === fee.student_id);
    if (!student?.phone) { toast.error('Student phone not found'); return; }
    const url = getWhatsAppReminderUrl(student.phone, student.name, fee.amount, fee.month, fee.year, fee.type);
    window.open(url, '_blank');
    toast.success(`WhatsApp opened for ${student.name}`);
  };

  const bulkReminders = getBulkReminderData(fees, students);

  // Success Story form
  const [storyForm, setStoryForm] = useState({ student_name: '', exam: '', post: '', year: '', photo_url: '', details: '' });
  const [storyPhotoFile, setStoryPhotoFile] = useState(null);
  const handleAddStory = async (e) => {
    e.preventDefault();
    try {
      let photoUrl = '';
      if (storyPhotoFile) {
        const formData = new FormData();
        formData.append('file', storyPhotoFile);
        const uploadRes = await apiCall('post', '/upload', formData);
        photoUrl = uploadRes.data.path;
      }
      await apiCall('post', '/success-stories', { ...storyForm, photo_url: photoUrl });
      toast.success('Success story added');
      setShowAddStory(false);
      setStoryForm({ student_name: '', exam: '', post: '', year: '', photo_url: '', details: '' });
      setStoryPhotoFile(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteStory = async (name) => {
    if (!window.confirm(`Delete ${name}'s story?`)) return;
    try { await apiCall('delete', `/success-stories/${encodeURIComponent(name)}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  // Teacher form
  const [teacherForm, setTeacherForm] = useState({ name: '', specialization: '', experience: '', photo_url: '', details: '' });
  const [teacherPhotoFile, setTeacherPhotoFile] = useState(null);
  const handleAddTeacher = async (e) => {
    e.preventDefault();
    try {
      let photoUrl = '';
      if (teacherPhotoFile) {
        const formData = new FormData();
        formData.append('file', teacherPhotoFile);
        const uploadRes = await apiCall('post', '/upload', formData);
        photoUrl = uploadRes.data.path;
      }
      await apiCall('post', '/teachers', { ...teacherForm, photo_url: photoUrl });
      toast.success('Teacher added');
      setShowAddTeacher(false);
      setTeacherForm({ name: '', specialization: '', experience: '', photo_url: '', details: '' });
      setTeacherPhotoFile(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteTeacher = async (name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await apiCall('delete', `/teachers/${encodeURIComponent(name)}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const pauseStudent = async (studentId, studentName) => {
    if (!window.confirm(`Pause ${studentName}? No fees will be generated while paused.`)) return;
    try { const res = await apiCall('post', `/students/${studentId}/pause`); toast.success(res.data.message); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const resumeStudent = async (studentId, studentName) => {
    if (!window.confirm(`Resume ${studentName}? Current month fee will be generated.`)) return;
    try { const res = await apiCall('post', `/students/${studentId}/resume`); toast.success(res.data.message); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const generateMonthlyFees = async () => {
    try {
      const res = await apiCall('post', '/fees/generate-monthly');
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleBulkAttendance = async () => {
    const records = Object.entries(attendanceRecords).map(([student_id, status]) => ({ student_id, status }));
    try {
      await apiCall('post', '/attendance/bulk', { batch_id: selectedBatch, date: attendanceDate, records });
      toast.success(`Attendance marked for ${records.length} students`);
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_signature-psc-portal/artifacts/h1jchf2q_IMG-20260126-WA0188.jpg" alt="Logo" className="w-8 h-8 rounded-md object-contain bg-white p-0.5" />
            <div>
              <span className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Admin Panel</span>
              <span className="text-[10px] block text-[#a1a1aa]">{user?.name}</span>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-[#a1a1aa] hover:text-white hover:bg-[#27272a]" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Branch/Location filter — Admin only */}
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="location-filter-row">
          <span className="text-[10px] uppercase tracking-wider text-[#a1a1aa] font-semibold mr-1">Branch:</span>
          <button onClick={() => setLocationFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${locationFilter === 'all' ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] border border-[#27272a] hover:border-[#d4af37]/40'}`}
            data-testid="loc-filter-all">All Branches</button>
          {locations.active.map(l => (
            <button key={l.slug} onClick={() => setLocationFilter(l.slug)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${locationFilter === l.slug ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] border border-[#27272a] hover:border-[#d4af37]/40'}`}
              data-testid={`loc-filter-${l.slug}`}>{l.name}</button>
          ))}
          {locations.upcoming.map(l => (
            <span key={l.slug} className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#18181b]/50 text-[#52525b] border border-[#27272a] cursor-not-allowed" data-testid={`loc-upcoming-${l.slug}`}>
              {l.name} <span className="text-[9px] uppercase ml-1 text-[#d4af37]/60">Soon</span>
            </span>
          ))}
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${tab === t.id ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] hover:bg-[#27272a] border border-[#27272a]'}`}
              data-testid={`admin-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === 'overview' && !stats && (
          <div className="p-12 rounded-lg bg-[#18181b] border border-[#27272a] text-center" data-testid="dashboard-loading">
            <div className="inline-block w-10 h-10 border-4 border-[#27272a] border-t-[#d4af37] rounded-full animate-spin mb-4"></div>
            <p className="text-white/70">Loading dashboard...</p>
            <p className="text-xs text-[#52525b] mt-2">If this stays for more than 10 seconds, check your internet connection.</p>
            <Button onClick={fetchData} className="mt-4 bg-[#d4af37] text-[#18181b] hover:bg-[#facc15]" data-testid="retry-dashboard">
              Retry
            </Button>
          </div>
        )}
        {tab === 'overview' && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            {/* SECTION 1: Financial Summary */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Financial Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <OverviewCard icon={CreditCard} label="Total Income" value={`₹${stats.total_income || 0}`} accent="#10b981" />
                <OverviewCard icon={Wallet} label="Total Expense" value={`₹${stats.total_expenses || 0}`} accent="#f59e0b" />
                <OverviewCard icon={BarChart3} label="Net Profit" value={`₹${stats.net_profit || 0}`} accent={(stats.net_profit || 0) >= 0 ? '#10b981' : '#ef4444'} />
                <OverviewCard icon={Send} label="Withdrawn" value={`₹${stats.total_withdrawn || 0}`} accent="#a78bfa" />
                <OverviewCard icon={CreditCard} label="Net Balance" value={`₹${stats.net_balance || 0}`} accent="#d4af37" />
              </div>
              <p className="text-[11px] text-[#52525b] mt-2">Net Profit = Income − Expenses · Net Balance = Net Profit − Withdrawn</p>
            </div>

            {/* SECTION 2: Cash / UPI / Bank Balance */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Balance by Payment Method</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="cash-balance">
                  <div className="flex items-center gap-2 mb-3"><Wallet className="w-5 h-5 text-emerald-400" /><span className="text-sm text-[#a1a1aa] font-medium uppercase tracking-wider">Cash</span></div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-xs text-[#52525b]">In</p><p className="text-sm font-bold text-emerald-400">₹{stats.income_by_method?.cash || 0}</p></div>
                    <div><p className="text-xs text-[#52525b]">Out</p><p className="text-sm font-bold text-red-400">₹{stats.expense_by_method?.cash || 0}</p></div>
                    <div><p className="text-xs text-[#52525b]">Balance</p><p className="text-lg font-bold text-white">₹{stats.balance_by_method?.cash || 0}</p></div>
                  </div>
                </div>
                <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="upi-balance">
                  <div className="flex items-center gap-2 mb-3"><CreditCard className="w-5 h-5 text-violet-400" /><span className="text-sm text-[#a1a1aa] font-medium uppercase tracking-wider">UPI</span></div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-xs text-[#52525b]">In</p><p className="text-sm font-bold text-emerald-400">₹{stats.income_by_method?.upi || 0}</p></div>
                    <div><p className="text-xs text-[#52525b]">Out</p><p className="text-sm font-bold text-red-400">₹{stats.expense_by_method?.upi || 0}</p></div>
                    <div><p className="text-xs text-[#52525b]">Balance</p><p className="text-lg font-bold text-white">₹{stats.balance_by_method?.upi || 0}</p></div>
                  </div>
                </div>
                <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="bank-balance">
                  <div className="flex items-center gap-2 mb-3"><CreditCard className="w-5 h-5 text-sky-400" /><span className="text-sm text-[#a1a1aa] font-medium uppercase tracking-wider">Bank Transfer</span></div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-xs text-[#52525b]">In</p><p className="text-sm font-bold text-emerald-400">₹{stats.income_by_method?.bank_transfer || 0}</p></div>
                    <div><p className="text-xs text-[#52525b]">Out</p><p className="text-sm font-bold text-red-400">₹{stats.expense_by_method?.bank_transfer || 0}</p></div>
                    <div><p className="text-xs text-[#52525b]">Balance</p><p className="text-lg font-bold text-white">₹{stats.balance_by_method?.bank_transfer || 0}</p></div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 3: Pending & Withdrawals */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Outstanding & Withdrawals</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <OverviewCard icon={Clock} label="Fee Pending" value={`₹${stats.total_pending}`} accent="#ef4444" />
                <OverviewCard icon={Send} label="Profit Withdrawn" value={`₹${stats.total_withdrawn || 0}`} accent="#8b5cf6" />
                <OverviewCard icon={MessageSquare} label="Enquiries" value={(stats.new_enquiries || 0) + (stats.new_contacts || 0)} accent="#d4af37" />
              </div>
            </div>

            {/* SECTION 4: Expense Breakdown */}
            {stats.expense_by_category && Object.keys(stats.expense_by_category).length > 0 && (
              <div className="p-6 rounded-lg bg-[#18181b] border border-[#27272a]">
                <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Expense Breakdown</h2>
                <div className="space-y-2">
                  {Object.entries(stats.expense_by_category).sort((a,b) => b[1]-a[1]).map(([cat, amt]) => (
                    <div key={cat} className="flex justify-between items-center py-2 border-b border-[#27272a] last:border-0">
                      <span className="text-sm text-[#a1a1aa] capitalize">{cat.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-bold text-[#f59e0b]">₹{amt}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SECTION 5: Students & Batches */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Students & Batches</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <OverviewCard icon={Users} label="Active Students" value={stats.total_students} accent="#d4af37" />
                {batches.map((b, i) => (
                  <div key={i} className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid={`batch-stat-${b.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-4 h-4 text-[#d4af37]" />
                      <span className="text-sm text-[#a1a1aa]">{b.name}</span>
                    </div>
                    <p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {stats.batch_counts?.[b.id] || 0}
                    </p>
                    <p className="text-xs text-[#52525b]">{b.timing}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* STUDENTS */}
        {tab === 'students' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
                <Input placeholder="Search students..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-10 bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]"
                  data-testid="admin-student-search" />
              </div>
              <Button onClick={() => setShowRegister(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="admin-register-btn">
                <UserPlus className="w-4 h-4 mr-2" /> New Admission
              </Button>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="admin-students-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['ID', 'Name', 'Phone', 'Batch', 'Branch', 'Plan', 'Status', 'Actions'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((s, i) => (
                      <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                        <td className="p-4 text-sm text-[#d4af37] font-mono">{s.student_id}</td>
                        <td className="p-4 text-sm text-white">{s.name}</td>
                        <td className="p-4 text-sm text-[#a1a1aa]">{s.phone}</td>
                        <td className="p-4 text-sm text-[#a1a1aa] capitalize">{s.batch_id}</td>
                        <td className="p-4">
                          <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${s.location === 'vizhavoor' ? 'bg-purple-500/15 text-purple-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                            {s.location || 'palachira'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-[#a1a1aa]">
                          {s.monthly_fee_waived ? (
                            <span className="text-purple-300 font-semibold">WAIVED</span>
                          ) : (
                            <>₹{s.monthly_fee || 800}</>
                          )}
                        </td>
                        <td className="p-4">
                          <Badge className={
                            s.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                            s.status === 'paused' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                            s.status === 'dismissed' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                            'bg-[#27272a]/30 text-[#a1a1aa] border-[#27272a]'
                          }>
                            {s.status === 'paused' ? 'suspended' : s.status}
                          </Badge>
                          {s.status === 'dismissed' && s.dismiss_reason && (
                            <p className="text-[9px] text-red-300 mt-1 italic truncate max-w-[140px]" title={s.dismiss_reason}>{s.dismiss_reason}</p>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1 flex-wrap">
                            <Button size="sm" variant="ghost" onClick={() => openPayFee(s.student_id)}
                              className="text-[#d4af37] hover:bg-[#d4af37]/10 text-xs" data-testid={`student-fee-${s.student_id}`}>
                              <CreditCard className="w-3 h-3 mr-1" /> Fee
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditStudent(s)}
                              className="text-blue-400 hover:bg-blue-500/10 text-xs" data-testid={`edit-student-${s.student_id}`}>
                              <Edit className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            {s.status === 'active' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => pauseStudent(s.student_id, s.name)}
                                  className="text-amber-400 hover:bg-amber-500/10 text-xs" data-testid={`suspend-${s.student_id}`}>
                                  Suspend
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => dismissStudent(s)}
                                  className="text-red-400 hover:bg-red-500/10 text-xs" data-testid={`dismiss-${s.student_id}`}>
                                  Dismiss
                                </Button>
                              </>
                            )}
                            {(s.status === 'paused' || s.status === 'dismissed') && (
                              <Button size="sm" variant="ghost" onClick={() => resumeStudent(s.student_id, s.name)}
                                className="text-emerald-400 hover:bg-emerald-500/10 text-xs" data-testid={`resume-${s.student_id}`}>
                                ↺ Re-Activate
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteStudent(s)}
                              className="text-red-500 hover:bg-red-500/10 text-xs" data-testid={`delete-student-${s.student_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* BATCHES */}
        {tab === 'batches' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="admin-batches-tab">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Manage Batches</h2>
                <p className="text-xs text-[#a1a1aa] mt-1">Add, edit, or delete batch timings. Batches with active students cannot be deleted.</p>
              </div>
              <Button onClick={openCreateBatch} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="admin-add-batch">
                <Plus className="w-4 h-4 mr-2" /> New Batch
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {batches.map((b) => {
                const count = students.filter(s => s.batch_id === b.id && s.status === 'active').length;
                return (
                  <div key={b.id} className="p-5 rounded-lg bg-[#18181b] border border-[#27272a] hover:border-[#d4af37]/30 transition-all" data-testid={`batch-card-${b.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{b.name}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-semibold">{count} active</span>
                    </div>
                    <p className="text-sm text-[#d4af37] font-medium mb-1">{b.timing || '—'}</p>
                    <p className="text-xs text-[#a1a1aa] mb-3">{b.description || 'No description'}</p>
                    <p className="text-[10px] text-[#52525b] font-mono mb-3">id: {b.id}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEditBatch(b)} className="text-blue-400 hover:bg-blue-500/10 text-xs" data-testid={`edit-batch-${b.id}`}>
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteBatch(b)} className="text-red-500 hover:bg-red-500/10 text-xs" data-testid={`delete-batch-${b.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
              {batches.length === 0 && (
                <div className="col-span-full text-center py-10 text-[#52525b]">
                  <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No batches yet. Click "New Batch" to create one.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* FEES */}
        {tab === 'fees' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-wrap gap-4">
              <Button onClick={generateMonthlyFees} className="bg-[#047857] text-white hover:bg-[#059669] font-semibold" data-testid="admin-generate-fees">
                <CreditCard className="w-4 h-4 mr-2" /> Generate Monthly Fees
              </Button>
              <Button onClick={() => setShowBulkReminder(true)} className="bg-[#25D366] text-white hover:bg-[#128C7E] font-semibold" data-testid="admin-bulk-whatsapp">
                <Send className="w-4 h-4 mr-2" /> WhatsApp Reminders
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Total Collected</p>
                <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                  ₹{fees.filter(f => f.status === 'paid').reduce((s, f) => s + f.amount, 0)}
                </p>
              </div>
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Total Pending</p>
                <p className="text-2xl font-bold text-red-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                  ₹{fees.filter(f => f.status === 'pending').reduce((s, f) => s + f.amount, 0)}
                </p>
              </div>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>All Fee Records</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Student ID', 'Month', 'Amount', 'Type', 'Status', 'Action'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fees.map((f, i) => (
                      <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                        <td className="p-4 text-sm text-[#d4af37] font-mono">{f.student_id}</td>
                        <td className="p-4 text-sm text-white">{f.month} {f.year}</td>
                        <td className="p-4 text-sm text-white font-medium">
                          ₹{f.amount}
                          {(f.status === 'partial' || (f.amount_paid && f.amount_paid > 0)) && (
                            <span className="block text-[10px] text-emerald-400">Paid ₹{f.amount_paid || 0} · Bal ₹{Math.max(0, (f.amount || 0) - (f.amount_paid || 0))}</span>
                          )}
                        </td>
                        <td className="p-4 text-sm text-[#a1a1aa] capitalize">{f.type}</td>
                        <td className="p-4">
                          <Badge className={
                            f.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                            f.status === 'partial' ? 'bg-blue-500/10 text-blue-300 border-blue-500/30' :
                            f.status === 'waived' ? 'bg-purple-500/10 text-purple-300 border-purple-500/30' :
                            'bg-amber-500/10 text-amber-400 border-amber-500/30'
                          }>
                            {f.status === 'paid' ? <CheckCircle className="w-3 h-3 mr-1" /> :
                             f.status === 'partial' ? <Clock className="w-3 h-3 mr-1" /> :
                             f.status === 'waived' ? <XCircle className="w-3 h-3 mr-1" /> :
                             <Clock className="w-3 h-3 mr-1" />}
                            {f.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-1 flex-wrap items-center">
                            {(f.status === 'pending' || f.status === 'partial') && (
                              <>
                                <Button size="sm" onClick={() => openPayFee(f.student_id)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] text-xs font-semibold" data-testid={`fee-collect-${f.student_id}-${f.month}`}>
                                  Collect
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => sendWhatsAppReminder(f)}
                                  className="text-[#25D366] hover:bg-[#25D366]/10 text-xs" data-testid={`admin-wa-${f.student_id}`}>
                                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => openEditFee(f)}
                              className="text-blue-400 hover:bg-blue-500/10 text-xs" data-testid={`edit-fee-${f.id}`}>
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteFee(f)}
                              className="text-red-500 hover:bg-red-500/10 text-xs" data-testid={`delete-fee-${f.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            {f.status === 'waived' && f.waived_reason && (
                              <span className="text-[10px] text-purple-300 italic ml-1">{f.waived_reason}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* ATTENDANCE */}
        {tab === 'attendance' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="w-48">
                <Label className="text-[#a1a1aa] text-xs uppercase tracking-wider mb-1 block">Date</Label>
                <Input type="date" value={attendanceDate} onChange={e => setAttendanceDate(e.target.value)}
                  className="bg-[#18181b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="admin-attendance-date" />
              </div>
              <div className="w-48">
                <Label className="text-[#a1a1aa] text-xs uppercase tracking-wider mb-1 block">Batch</Label>
                <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                  <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white" data-testid="admin-attendance-batch">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {batches.map(b => (<SelectItem key={b.id} value={b.id} className="text-white hover:bg-[#27272a]">{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:mt-5">
                <Button onClick={handleBulkAttendance} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="admin-save-attendance">
                  <CheckCircle className="w-4 h-4 mr-2" /> Save Attendance
                </Button>
              </div>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Student ID', 'Name', 'Status'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {batchStudents.length === 0 ? (
                      <tr><td colSpan={3} className="p-8 text-center text-[#52525b]">No students in this batch</td></tr>
                    ) : (
                      batchStudents.map((s, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-[#d4af37] font-mono">{s.student_id}</td>
                          <td className="p-4 text-sm text-white">{s.name}</td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              {['present', 'late', 'leave', 'absent'].map(status => (
                                <button key={status} onClick={() => setAttendanceRecords(prev => ({ ...prev, [s.student_id]: status }))}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-all ${attendanceRecords[s.student_id] === status ? (status === 'present' ? 'bg-emerald-500 text-white' : status === 'late' ? 'bg-amber-500 text-white' : status === 'leave' ? 'bg-violet-500 text-white' : 'bg-red-500 text-white') : 'bg-[#27272a] text-[#a1a1aa] hover:bg-[#3f3f46]'}`}>
                                  {status}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* OTHER INCOME TAB */}
        {tab === 'income' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Other Income</h2>
              <Button onClick={() => setShowAddIncome(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="add-income-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Income
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Total Other Income</p>
                <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{incomes.reduce((s, i) => s + i.amount, 0)}</p>
              </div>
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">This Month</p>
                <p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>
                  ₹{incomes.filter(i => i.date?.startsWith(`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`)).reduce((s, i) => s + i.amount, 0)}
                </p>
              </div>
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Entries</p>
                <p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>{incomes.length}</p>
              </div>
            </div>
            {/* Category Breakdown */}
            {stats?.other_income_by_category && Object.keys(stats.other_income_by_category).length > 0 && (
              <div className="p-6 rounded-lg bg-[#18181b] border border-[#27272a]" data-testid="income-category-breakdown">
                <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>By Category (All Time)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {INCOME_CATEGORIES.map(c => (
                    <div key={c.id} className="p-3 rounded-md bg-[#0f0f12] border border-[#27272a] text-center">
                      <p className="text-xs text-[#a1a1aa] uppercase tracking-wider">{c.label}</p>
                      <p className="text-lg font-bold text-emerald-400 mt-1">₹{stats.other_income_by_category[c.id] || 0}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="incomes-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Date', 'Category', 'Amount', 'Description', 'Method', 'Added By', 'Actions'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {incomes.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-[#52525b]">No other income recorded</td></tr>
                    ) : (
                      incomes.map((i, idx) => (
                        <tr key={idx} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{i.date}</td>
                          <td className="p-4 text-sm text-[#d4af37] capitalize">{i.category}</td>
                          <td className="p-4 text-sm text-emerald-400 font-bold">₹{i.amount}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] max-w-[200px] truncate">{i.description || '-'}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{i.payment_method}</td>
                          <td className="p-4 text-sm text-[#52525b]">{i.added_by}</td>
                          <td className="p-4">
                            <Button size="sm" variant="ghost" onClick={() => deleteFinanceRecord('other-income', i.id, 'income')}
                              className="text-red-400 hover:bg-red-500/10 text-xs" data-testid={`del-income-${i.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* EXPENSES TAB */}
        {tab === 'expenses' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Expense Management</h2>
              <Button onClick={() => setShowAddExpense(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="add-expense-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Expense
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Total Expenses</p>
                <p className="text-2xl font-bold text-[#f59e0b]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{expenses.reduce((s, e) => s + e.amount, 0)}</p>
              </div>
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">This Month</p>
                <p className="text-2xl font-bold text-[#f59e0b]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  ₹{expenses.filter(e => e.date?.startsWith(`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`)).reduce((s, e) => s + e.amount, 0)}
                </p>
              </div>
              <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Entries</p>
                <p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>{expenses.length}</p>
              </div>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="expenses-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Date', 'Category', 'Amount', 'Description', 'Method', 'Added By', 'Actions'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-[#52525b]">No expenses recorded</td></tr>
                    ) : (
                      expenses.map((e, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{e.date}</td>
                          <td className="p-4 text-sm text-[#d4af37] capitalize">{e.category?.replace(/_/g, ' ')}</td>
                          <td className="p-4 text-sm text-[#f59e0b] font-bold">₹{e.amount}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] max-w-[200px] truncate">{e.description || '-'}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{e.payment_method}</td>
                          <td className="p-4 text-sm text-[#52525b]">{e.added_by}</td>
                          <td className="p-4">
                            <Button size="sm" variant="ghost" onClick={() => deleteFinanceRecord('expenses', e.id, 'expense')}
                              className="text-red-400 hover:bg-red-500/10 text-xs" data-testid={`del-expense-${e.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* PROFIT SHARING TAB */}
        {tab === 'profit' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Partner Profit Sharing</h2>
              <Button onClick={() => setShowWithdrawProfit(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="withdraw-profit-btn">
                <Wallet className="w-4 h-4 mr-2" /> Record Withdrawal
              </Button>
            </div>

            {profitSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                  <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Net Profit</p>
                  <p className={`text-2xl font-bold ${profitSummary.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} style={{ fontFamily: "'Playfair Display', serif" }}>₹{profitSummary.net_profit}</p>
                  <p className="text-xs text-[#52525b] mt-1">Income ₹{profitSummary.total_income} - Expenses ₹{profitSummary.total_expenses}</p>
                </div>
                <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                  <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Total Withdrawn</p>
                  <p className="text-2xl font-bold text-violet-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{profitSummary.total_withdrawn}</p>
                </div>
                <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                  <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">{profitSummary.partner1_name}</p>
                  <p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{profitSummary.partner1_total}</p>
                </div>
                <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]">
                  <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">{profitSummary.partner2_name}</p>
                  <p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{profitSummary.partner2_total}</p>
                </div>
              </div>
            )}

            {/* Balance card */}
            {profitSummary && (
              <div className="p-6 rounded-lg bg-[#18181b] border border-[#d4af37]/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#a1a1aa] uppercase tracking-wider">Remaining Balance (After Withdrawals)</p>
                    <p className={`text-3xl font-bold mt-1 ${profitSummary.balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`} style={{ fontFamily: "'Playfair Display', serif" }}>₹{profitSummary.balance}</p>
                  </div>
                  <Wallet className="w-10 h-10 text-[#d4af37]/30" />
                </div>
              </div>
            )}

            {/* Withdrawal History */}
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="withdrawals-table">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Withdrawal History</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Date', 'Month', 'Partner 1', 'Amount', 'Partner 2', 'Amount', 'Total', 'Notes'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.length === 0 ? (
                      <tr><td colSpan={8} className="p-8 text-center text-[#52525b]">No withdrawals recorded yet</td></tr>
                    ) : (
                      withdrawals.map((w, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{w.date}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{w.month} {w.year}</td>
                          <td className="p-4 text-sm text-[#d4af37]">{w.partner1_name}</td>
                          <td className="p-4 text-sm text-white font-bold">₹{w.partner1_amount}</td>
                          <td className="p-4 text-sm text-[#d4af37]">{w.partner2_name}</td>
                          <td className="p-4 text-sm text-white font-bold">₹{w.partner2_amount}</td>
                          <td className="p-4 text-sm text-violet-400 font-bold">₹{w.total_withdrawn}</td>
                          <td className="p-4 text-sm text-[#52525b] max-w-[150px] truncate">{w.notes || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* SUCCESS CORNER TAB */}
        {tab === 'success' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Success Corner</h2>
              <Button onClick={() => setShowAddStory(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="add-story-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Success Story
              </Button>
            </div>
            <p className="text-sm text-[#a1a1aa]">Manage students who got government jobs. These are displayed on the homepage.</p>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="stories-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#27272a]">
                    {['Photo', 'Name', 'Exam', 'Post', 'Year', 'Details', 'Actions'].map(h => (<th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {successStories.length === 0 ? (
                      <tr><td colSpan={7} className="p-8 text-center text-[#52525b]">No success stories yet. Add your first one!</td></tr>
                    ) : successStories.map((s, i) => (
                      <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                        <td className="p-4">{s.photo_url ? <img src={s.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 flex items-center justify-center"><Trophy className="w-4 h-4 text-[#d4af37]" /></div>}</td>
                        <td className="p-4 text-sm text-white font-medium">{s.student_name}</td>
                        <td className="p-4 text-sm text-[#a1a1aa]">{s.exam}</td>
                        <td className="p-4 text-sm text-[#d4af37]">{s.post}</td>
                        <td className="p-4 text-sm text-[#a1a1aa]">{s.year}</td>
                        <td className="p-4 text-sm text-[#a1a1aa] max-w-[150px] truncate">{s.details || '-'}</td>
                        <td className="p-4"><Button size="sm" variant="ghost" onClick={() => deleteStory(s.student_name)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="w-3 h-3" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* TEACHERS TAB */}
        {tab === 'faculty' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Teachers Management</h2>
              <Button onClick={() => setShowAddTeacher(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="add-teacher-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Teacher
              </Button>
            </div>
            <p className="text-sm text-[#a1a1aa]">Manage teacher profiles. These are displayed on the homepage.</p>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="teachers-admin-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#27272a]">
                    {['Photo', 'Name', 'Specialization', 'Experience', 'Details', 'Actions'].map(h => (<th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {teachersList.length === 0 ? (
                      <tr><td colSpan={6} className="p-8 text-center text-[#52525b]">No teachers added yet.</td></tr>
                    ) : teachersList.map((t, i) => (
                      <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                        <td className="p-4">{t.photo_url ? <img src={t.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[#d4af37]/10 flex items-center justify-center"><Briefcase className="w-4 h-4 text-[#d4af37]" /></div>}</td>
                        <td className="p-4 text-sm text-white font-medium">{t.name}</td>
                        <td className="p-4 text-sm text-[#d4af37]">{t.specialization}</td>
                        <td className="p-4 text-sm text-[#a1a1aa]">{t.experience}</td>
                        <td className="p-4 text-sm text-[#a1a1aa] max-w-[150px] truncate">{t.details || '-'}</td>
                        <td className="p-4"><Button size="sm" variant="ghost" onClick={() => deleteTeacher(t.name)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="w-3 h-3" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* REPORTS TAB */}
        {/* CONTENT (CMS) TAB */}
        {tab === 'content' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="content-tab">
            <div>
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Website Content</h2>
              <p className="text-sm text-[#a1a1aa] mt-1">Daily current affairs, PSC notifications, mock questions & short videos appear in the Updates section of your website.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'current_affairs', label: 'Daily Current Affairs' },
                { id: 'notification', label: 'PSC Notifications' },
                { id: 'mock_question', label: 'Mock Questions' },
                { id: 'video', label: 'Short Videos' },
              ].map(t => (
                <button key={t.id} onClick={() => { setCmsTab(t.id); setCmsForm({ type: t.id, title: '', content: '', link: '', options: ['','','',''], correct_index: 0, explanation: '' }); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${cmsTab === t.id ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] border border-[#27272a]'}`}
                  data-testid={`cms-tab-${t.id}`}>{t.label}</button>
              ))}
            </div>
            {/* Add Form */}
            <form onSubmit={handleAddCmsPost} className="p-5 rounded-lg bg-[#18181b] border border-[#27272a] space-y-3" data-testid="cms-form">
              <Input placeholder="Title (e.g. Kerala PSC LDC 2026 Notification released)" value={cmsForm.title} onChange={e => setCmsForm({ ...cmsForm, title: e.target.value })} required
                className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="cms-title" />
              {cmsTab !== 'video' && cmsTab !== 'mock_question' && (
                <Textarea placeholder="Content / description (multi-line)" value={cmsForm.content} onChange={e => setCmsForm({ ...cmsForm, content: e.target.value })} rows={4}
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="cms-content" />
              )}
              {cmsTab === 'video' && (
                <Input placeholder="YouTube URL (e.g. https://youtube.com/watch?v=...)" value={cmsForm.link} onChange={e => setCmsForm({ ...cmsForm, link: e.target.value })} required
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="cms-video-url" />
              )}
              {cmsTab !== 'video' && cmsTab !== 'mock_question' && (
                <Input placeholder="Optional external link (e.g. PSC notification URL)" value={cmsForm.link} onChange={e => setCmsForm({ ...cmsForm, link: e.target.value })}
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="cms-link" />
              )}
              {cmsTab === 'mock_question' && (
                <>
                  {cmsForm.options.map((opt, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input value={`${String.fromCharCode(65+i)}.`} readOnly className="w-12 bg-[#27272a] border-[#27272a] text-[#d4af37] text-center" />
                      <Input placeholder={`Option ${String.fromCharCode(65+i)}`} value={opt}
                        onChange={e => { const o=[...cmsForm.options]; o[i]=e.target.value; setCmsForm({ ...cmsForm, options: o }); }}
                        className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid={`cms-option-${i}`} />
                      <input type="radio" checked={cmsForm.correct_index === i} onChange={() => setCmsForm({ ...cmsForm, correct_index: i })}
                        className="w-5 h-5 accent-emerald-500" title="Mark as correct" data-testid={`cms-correct-${i}`} />
                    </div>
                  ))}
                  <Textarea placeholder="Explanation (optional)" value={cmsForm.explanation} onChange={e => setCmsForm({ ...cmsForm, explanation: e.target.value })} rows={2}
                    className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="cms-explanation" />
                </>
              )}
              <Button type="submit" className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="cms-submit">
                <Plus className="w-4 h-4 mr-2" /> Publish to Website
              </Button>
            </form>
            {/* List */}
            <div className="rounded-lg bg-[#18181b] border border-[#27272a]">
              <div className="p-4 border-b border-[#27272a]"><h3 className="text-base font-semibold text-white">Published — {cmsPosts.filter(p => p.type === cmsTab).length} entries</h3></div>
              <div className="divide-y divide-[#27272a]">
                {cmsPosts.filter(p => p.type === cmsTab).length === 0 ? (
                  <p className="p-6 text-center text-[#52525b] text-sm">No entries yet. Add your first one above.</p>
                ) : cmsPosts.filter(p => p.type === cmsTab).map(p => (
                  <div key={p.id} className="p-4 flex items-start justify-between gap-3" data-testid={`cms-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">{p.title}</p>
                      {p.content && <p className="text-xs text-[#a1a1aa] mt-1 line-clamp-2 whitespace-pre-wrap">{p.content}</p>}
                      {p.link && <a href={p.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#d4af37] mt-1 break-all">{p.link}</a>}
                      <p className="text-[10px] text-[#52525b] mt-1">{p.created_at?.split('T')[0]} · {p.added_by}</p>
                    </div>
                    <Button onClick={() => deleteCmsPost(p.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" data-testid={`cms-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* STUDY NOTES TAB */}
        {tab === 'notes' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="notes-tab">
            <div>
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Study Notes</h2>
              <p className="text-sm text-[#a1a1aa] mt-1">Upload PDF notes or write HTML content. Students see them in their portal.</p>
            </div>
            <form onSubmit={handleCreateNote} className="p-5 rounded-lg bg-[#18181b] border border-[#27272a] space-y-3" data-testid="note-form">
              {editingNoteId && (
                <div className="flex items-center justify-between p-2 rounded bg-amber-500/10 border border-amber-500/30">
                  <span className="text-xs text-amber-400 font-semibold">✎ Editing existing note</span>
                  <Button type="button" onClick={cancelNoteEdit} variant="ghost" size="sm" className="text-amber-400 hover:bg-amber-500/10 text-xs h-7" data-testid="note-cancel-edit">Cancel</Button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Title (e.g. Kerala History — Chapter 1)" value={noteForm.title} onChange={e => setNoteForm({ ...noteForm, title: e.target.value })} required
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="note-title" />
                <Input placeholder="Subject (e.g. GK, Maths, English)" value={noteForm.subject} onChange={e => setNoteForm({ ...noteForm, subject: e.target.value })}
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="note-subject" />
              </div>
              <Input placeholder="Short description (optional)" value={noteForm.description} onChange={e => setNoteForm({ ...noteForm, description: e.target.value })}
                className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="note-description" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[#a1a1aa] text-xs">Type</Label>
                  <Select value={noteForm.note_type} onValueChange={v => setNoteForm({ ...noteForm, note_type: v, pdf_path: '', html_content: '' })}>
                    <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="note-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">
                      <SelectItem value="pdf" className="text-white hover:bg-[#27272a]">PDF Upload</SelectItem>
                      <SelectItem value="html" className="text-white hover:bg-[#27272a]">HTML / Text</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#a1a1aa] text-xs">Visible to</Label>
                  <Select value={noteForm.visible_to} onValueChange={v => setNoteForm({ ...noteForm, visible_to: v })}>
                    <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="note-visible"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">
                      <SelectItem value="all" className="text-white hover:bg-[#27272a]">Everyone (public)</SelectItem>
                      <SelectItem value="students_only" className="text-white hover:bg-[#27272a]">Logged-in students only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#a1a1aa] text-xs">Required Plan</Label>
                  <Select value={noteForm.required_tier} onValueChange={v => setNoteForm({ ...noteForm, required_tier: v })}>
                    <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="note-tier"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">
                      <SelectItem value="free" className="text-white hover:bg-[#27272a]">Free (Trial)</SelectItem>
                      <SelectItem value="basic" className="text-white hover:bg-[#27272a]">Basic ₹199+</SelectItem>
                      <SelectItem value="mcq" className="text-white hover:bg-[#27272a]">MCQ ₹499+</SelectItem>
                      <SelectItem value="premium" className="text-white hover:bg-[#27272a]">Premium ₹999</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#a1a1aa] text-xs">Day (1-100)</Label>
                  <Input type="number" min="1" max="100" placeholder="e.g. 7" value={noteForm.day_number} onChange={e => setNoteForm({ ...noteForm, day_number: e.target.value })}
                    className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="note-day" />
                </div>
              </div>
              {noteForm.note_type === 'pdf' ? (
                <div>
                  <Label className="text-[#a1a1aa] text-xs">Upload PDF (max 25MB)</Label>
                  <Input type="file" accept="application/pdf" onChange={handleNotePdfUpload} disabled={noteUploading}
                    className="bg-[#09090b] border-[#27272a] text-white file:bg-[#d4af37] file:text-[#18181b] file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:text-xs file:font-semibold" data-testid="note-pdf-upload" />
                  {noteUploading && <p className="text-xs text-[#d4af37] mt-1">Uploading...</p>}
                  {noteForm.pdf_path && <p className="text-xs text-emerald-400 mt-1">✓ Uploaded: {noteForm.pdf_path}</p>}
                </div>
              ) : (
                <Textarea placeholder={`<h2>Topic title</h2>\n<p>Write your notes here. Basic HTML supported (b, i, h2, h3, ul, li, br).</p>`}
                  value={noteForm.html_content} onChange={e => setNoteForm({ ...noteForm, html_content: e.target.value })} rows={10}
                  className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] font-mono text-xs" data-testid="note-html" />
              )}
              <Button type="submit" disabled={noteUploading} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="note-submit">
                <Plus className="w-4 h-4 mr-2" /> {editingNoteId ? 'Update Note' : 'Publish Note'}
              </Button>
            </form>
            {/* List */}
            <div className="rounded-lg bg-[#18181b] border border-[#27272a]">
              <div className="p-4 border-b border-[#27272a]"><h3 className="text-base font-semibold text-white">Published Notes — {notes.length}</h3></div>
              <div className="divide-y divide-[#27272a]">
                {notes.length === 0 ? (
                  <p className="p-6 text-center text-[#52525b] text-sm">No notes yet.</p>
                ) : notes.map(n => (
                  <div key={n.id} className="p-4 flex items-start justify-between gap-3" data-testid={`note-row-${n.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-white font-medium">{n.title}</p>
                        {n.day_number && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 uppercase font-semibold">Day {n.day_number}</span>}
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#d4af37]/15 text-[#d4af37] uppercase">{n.note_type}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#27272a] text-[#a1a1aa]">{n.subject || 'General'}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-[#27272a] text-[#a1a1aa]">{n.visible_to === 'all' ? 'Public' : 'Students Only'}</span>
                        {n.required_tier && n.required_tier !== 'free' && <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/15 text-purple-300 uppercase">Tier: {n.required_tier}</span>}
                      </div>
                      {n.description && <p className="text-xs text-[#a1a1aa] mt-1">{n.description}</p>}
                      {n.note_type === 'pdf' && n.pdf_path && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${n.pdf_path}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-400 mt-1"><Download className="w-3 h-3" /> Open PDF</a>
                      )}
                      <p className="text-[10px] text-[#52525b] mt-1">{n.created_at?.split('T')[0]} · {n.added_by}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button onClick={() => editNote(n)} variant="ghost" size="sm" className="text-[#d4af37] hover:bg-[#d4af37]/10" data-testid={`note-edit-${n.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => deleteNote(n.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" data-testid={`note-delete-${n.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ONLINE SUBSCRIPTIONS TAB */}
        {tab === 'online' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="online-subs-tab">
            <div>
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Online Subscriptions</h2>
              <p className="text-sm text-[#a1a1aa] mt-1">Approve/reject payments for the Online Batch. Plans: ₹199 / ₹499 / ₹999 (100 days each).</p>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Total', count: onlineSubs.length, color: 'text-white' },
                { label: 'On Trial', count: onlineSubs.filter(s => s.status === 'pending_payment').length, color: 'text-[#d4af37]' },
                { label: 'Awaiting Approval', count: onlineSubs.filter(s => s.status === 'payment_submitted').length, color: 'text-amber-400' },
                { label: 'Approved', count: onlineSubs.filter(s => s.status === 'approved').length, color: 'text-emerald-400' },
                { label: 'Rejected', count: onlineSubs.filter(s => s.status === 'rejected').length, color: 'text-red-400' },
              ].map(s => (
                <div key={s.label} className="p-4 rounded-lg bg-[#18181b] border border-[#27272a] text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
                  <p className="text-[10px] text-[#a1a1aa] uppercase mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            {/* Filter */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'payment_submitted', label: 'Awaiting Approval' },
                { id: 'approved', label: 'Approved' },
                { id: 'pending_payment', label: 'On Trial' },
                { id: 'rejected', label: 'Rejected' },
                { id: 'all', label: 'All' },
              ].map(f => (
                <button key={f.id} onClick={() => setOnlineSubFilter(f.id)}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${onlineSubFilter === f.id ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] border border-[#27272a]'}`}
                  data-testid={`online-filter-${f.id}`}>{f.label}</button>
              ))}
            </div>
            {/* Table */}
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="online-subs-table">
              <div className="overflow-x-auto"><table className="w-full">
                <thead><tr className="border-b border-[#27272a]">
                  {['Date', 'Student', 'WhatsApp', 'Plan', 'Amount', 'UTR', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {onlineSubs.filter(s => onlineSubFilter === 'all' || s.status === onlineSubFilter).length === 0 ? (
                    <tr><td colSpan={8} className="p-8 text-center text-[#52525b]">No subscriptions in this category</td></tr>
                  ) : onlineSubs.filter(s => onlineSubFilter === 'all' || s.status === onlineSubFilter).map(s => (
                    <tr key={s.id} className="border-b border-[#27272a] last:border-0" data-testid={`online-row-${s.id}`}>
                      <td className="p-3 text-xs text-[#a1a1aa] whitespace-nowrap">{s.created_at?.split('T')[0]}</td>
                      <td className="p-3 text-sm text-white font-medium whitespace-nowrap">{s.name}<span className="block text-[10px] text-[#52525b] font-mono">{s.student_id}</span></td>
                      <td className="p-3 text-sm">
                        <a href={`https://wa.me/91${s.phone}?text=Hi ${s.name}, regarding your Signature PSC Online Batch subscription.`} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">{s.phone}</a>
                      </td>
                      <td className="p-3 text-xs text-[#d4af37] uppercase whitespace-nowrap">{s.plan}</td>
                      <td className="p-3 text-sm text-[#d4af37] font-bold whitespace-nowrap">₹{s.amount_due}</td>
                      <td className="p-3 text-xs font-mono text-amber-400 whitespace-nowrap">{s.utr_number || '-'}</td>
                      <td className="p-3 text-xs whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded uppercase font-semibold text-[10px] ${
                          s.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                          s.status === 'payment_submitted' ? 'bg-amber-500/20 text-amber-300' :
                          s.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                          'bg-white/10 text-white/70'
                        }`}>{s.status?.replace(/_/g, ' ')}</span>
                        {s.expires_at && s.status === 'approved' && <p className="text-[10px] text-[#52525b] mt-1">Exp: {s.expires_at.split('T')[0]}</p>}
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        {s.status === 'payment_submitted' && (
                          <div className="flex gap-1">
                            <Button onClick={() => approveSub(s.id)} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7" data-testid={`approve-${s.id}`}><CheckCircle className="w-3 h-3 mr-1" />Approve</Button>
                            <Button onClick={() => rejectSub(s.id)} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs h-7 bg-transparent" data-testid={`reject-${s.id}`}><XCircle className="w-3 h-3" /></Button>
                          </div>
                        )}
                        {s.status !== 'payment_submitted' && (
                          <a href={`https://wa.me/91${s.phone}?text=Hi ${s.name}, your Signature PSC subscription is ${s.status}.`} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 hover:underline flex items-center gap-1">
                            <Send className="w-3 h-3" /> WhatsApp
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          </motion.div>
        )}

        {/* DAILY QUIZ TAB */}
        {tab === 'quiz' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="quiz-tab">
            <div>
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Daily Mock Test</h2>
              <p className="text-sm text-[#a1a1aa] mt-1">Upload 50 questions, students attempt from their panel, guests attempt at <a href="/daily-mock-test" target="_blank" rel="noopener noreferrer" className="text-[#d4af37] hover:underline">/daily-mock-test</a> (guest marks = marketing leads).</p>
            </div>

            {/* Sub-tabs */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'upload', label: 'Upload / Quizzes' },
                { id: 'student', label: `Student Attempts (${quizAttempts.filter(a => a.user_type === 'student').length})` },
                { id: 'guest', label: `Guest Leads (${quizAttempts.filter(a => a.user_type === 'guest').length})` },
              ].map(t => (
                <button key={t.id} onClick={() => setQuizSubTab(t.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${quizSubTab === t.id ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] border border-[#27272a]'}`}
                  data-testid={`quiz-subtab-${t.id}`}>{t.label}</button>
              ))}
            </div>

            {/* Upload form */}
            {quizSubTab === 'upload' && (
              <>
                <form onSubmit={handleCreateQuiz} className="p-5 rounded-lg bg-[#18181b] border border-[#27272a] space-y-4" data-testid="quiz-form">
                  {editingQuizId && (
                    <div className="flex items-center justify-between p-2 rounded bg-amber-500/10 border border-amber-500/30">
                      <span className="text-xs text-amber-400 font-semibold">✎ Editing existing quiz</span>
                      <Button type="button" onClick={cancelQuizEdit} variant="ghost" size="sm" className="text-amber-400 hover:bg-amber-500/10 text-xs h-7" data-testid="quiz-cancel-edit">Cancel</Button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[#a1a1aa] text-xs uppercase">Quiz Date *</Label>
                      <Input type="date" value={quizForm.date} onChange={e => setQuizForm({ ...quizForm, date: e.target.value })} required
                        className="bg-[#09090b] border-[#27272a] text-white" data-testid="quiz-date" />
                    </div>
                    <div>
                      <Label className="text-[#a1a1aa] text-xs uppercase">Day (1-100)</Label>
                      <Input type="number" min="1" max="100" placeholder="e.g. 7" value={quizForm.day_number} onChange={e => setQuizForm({ ...quizForm, day_number: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="quiz-day" />
                    </div>
                    <div>
                      <Label className="text-[#a1a1aa] text-xs uppercase">Title (Optional)</Label>
                      <Input value={quizForm.title} onChange={e => setQuizForm({ ...quizForm, title: e.target.value })} placeholder="e.g. LDC Mock Test - Day 47"
                        className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b]" data-testid="quiz-title" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-lg bg-[#09090b]/50 border border-[#27272a]/50">
                    <div className="col-span-2 md:col-span-2">
                      <Label className="text-[#d4af37] text-xs uppercase font-semibold flex items-center gap-1"><Target className="w-3 h-3" /> PSC Marking Scheme</Label>
                      <p className="text-[10px] text-[#52525b] mt-0.5">Default: +1 correct, -0.33 wrong. Set negative to 0 to disable.</p>
                    </div>
                    <div>
                      <Label className="text-[#a1a1aa] text-[10px] uppercase">Positive Marks (correct)</Label>
                      <Input type="number" step="0.01" min="0" value={quizForm.positive_marks} onChange={e => setQuizForm({ ...quizForm, positive_marks: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-white" data-testid="quiz-positive-marks" />
                    </div>
                    <div>
                      <Label className="text-[#a1a1aa] text-[10px] uppercase">Negative Marks (wrong)</Label>
                      <Input type="number" step="0.01" min="0" value={quizForm.negative_marks} onChange={e => setQuizForm({ ...quizForm, negative_marks: e.target.value })}
                        className="bg-[#09090b] border-[#27272a] text-white" data-testid="quiz-negative-marks" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#a1a1aa] text-xs uppercase mb-2 block">Bulk Paste Questions *</Label>
                    <Textarea value={quizForm.bulk_text} onChange={e => setQuizForm({ ...quizForm, bulk_text: e.target.value })} required rows={14}
                      placeholder={`1. What is the capital of Kerala?
A) Kochi
B) Thiruvananthapuram*
C) Kozhikode
D) Thrissur
E: Thiruvananthapuram is the capital.
---
2. Consider the following statements regarding Kerala:
S1) Kerala has the highest literacy rate in India.
S2) Kerala was formed on 1st November 1956.
Which of the above is/are correct?
A) Only S1
B) Only S2
C) Both S1 and S2*
D) Neither S1 nor S2
E: Both statements are factually correct about Kerala.`}
                      className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] font-mono text-xs" data-testid="quiz-bulk" />
                    <p className="text-[11px] text-[#52525b] mt-2 leading-relaxed">
                      <strong className="text-[#d4af37]">Format:</strong> Start with <code className="text-[#d4af37]">1.</code> or <code className="text-[#d4af37]">Q:</code>. For statement-type questions, write each statement on a new line with <code className="text-emerald-400">S1) S2) S3)</code>. Options use <code className="text-[#d4af37]">A) B) C) D)</code> — mark correct with <strong className="text-emerald-400">*</strong>. <code className="text-[#d4af37]">E:</code> for explanation. Separate questions with <code className="text-[#d4af37]">---</code>.
                    </p>
                  </div>
                  <Button type="submit" className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="quiz-submit">
                    <Plus className="w-4 h-4 mr-2" /> {editingQuizId ? 'Update Quiz' : 'Publish Quiz'}
                  </Button>
                </form>

                {/* List of Quizzes */}
                <div className="rounded-lg bg-[#18181b] border border-[#27272a]">
                  <div className="p-4 border-b border-[#27272a]"><h3 className="text-base font-semibold text-white">Published Quizzes — {quizzes.length}</h3></div>
                  <div className="divide-y divide-[#27272a]">
                    {quizzes.length === 0 ? (
                      <p className="p-6 text-center text-[#52525b] text-sm">No quizzes yet. Upload one above.</p>
                    ) : quizzes.map(q => (
                      <div key={q.id} className="p-4 flex items-start justify-between gap-3" data-testid={`quiz-row-${q.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm text-white font-medium">{q.title}</p>
                            {q.day_number && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 uppercase font-semibold">Day {q.day_number}</span>}
                          </div>
                          <p className="text-xs text-[#a1a1aa] mt-1">
                            {q.date} · {q.questions.length} questions · {q.student_attempts} student attempts · {q.guest_attempts} guest leads
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button onClick={() => editQuiz(q)} variant="ghost" size="sm" className="text-[#d4af37] hover:bg-[#d4af37]/10" data-testid={`quiz-edit-${q.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button onClick={() => deleteQuiz(q.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" data-testid={`quiz-delete-${q.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Student attempts table */}
            {(quizSubTab === 'student' || quizSubTab === 'guest') && (
              <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid={`attempts-${quizSubTab}`}>
                <div className="p-4 border-b border-[#27272a] flex justify-between items-center">
                  <h3 className="text-base font-semibold text-white">{quizSubTab === 'student' ? 'Student Attempts' : 'Guest Leads (from outside aspirants)'}</h3>
                  {quizSubTab === 'guest' && quizAttempts.filter(a => a.user_type === 'guest').length > 0 && (
                    <Button onClick={() => {
                      const guests = quizAttempts.filter(a => a.user_type === 'guest');
                      const csv = 'Name,Phone,Score,Total,%,Quiz Date,Submitted\n' +
                        guests.map(g => `"${g.guest_name}","${g.guest_phone}",${g.score},${g.total},${g.percentage},${g.quiz_date},${g.submitted_at?.split('T')[0]}`).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = `mock_test_leads_${new Date().toISOString().split('T')[0]}.csv`; a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Leads CSV downloaded');
                    }} className="bg-[#047857] hover:bg-[#059669] text-white text-xs" data-testid="download-leads-csv">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Export Leads CSV
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto"><table className="w-full">
                  <thead><tr className="border-b border-[#27272a]">
                    {(quizSubTab === 'student' ? ['Date', 'Student', 'Score', '%', 'Right/Wrong/Skip', 'Submitted']
                                                : ['Date', 'Name', 'WhatsApp', 'Score', '%', 'Action']).map(h => (
                      <th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {quizAttempts.filter(a => a.user_type === quizSubTab).length === 0 ? (
                      <tr><td colSpan={6} className="p-8 text-center text-[#52525b]">No attempts yet</td></tr>
                    ) : quizAttempts.filter(a => a.user_type === quizSubTab).map(a => (
                      <tr key={a.id} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30" data-testid={`attempt-row-${a.id}`}>
                        <td className="p-3 text-sm text-[#a1a1aa]">{a.quiz_date}</td>
                        {quizSubTab === 'student' ? (
                          <>
                            <td className="p-3 text-sm text-white font-medium">{a.student_name || '-'} <span className="text-[10px] text-[#52525b] ml-1">{a.student_id}</span></td>
                            <td className="p-3 text-sm text-[#d4af37] font-bold">{a.score} / {a.total}</td>
                            <td className={`p-3 text-sm font-bold ${a.percentage >= 60 ? 'text-emerald-400' : a.percentage >= 33 ? 'text-amber-400' : 'text-red-400'}`}>{a.percentage}%</td>
                            <td className="p-3 text-xs text-[#a1a1aa]"><span className="text-emerald-400">{a.correct}</span>/<span className="text-red-400">{a.wrong}</span>/<span className="text-white/50">{a.skipped}</span></td>
                            <td className="p-3 text-xs text-[#52525b]">{a.submitted_at?.split('T')[0]}</td>
                          </>
                        ) : (
                          <>
                            <td className="p-3 text-sm text-white font-medium">{a.guest_name}</td>
                            <td className="p-3 text-sm">
                              <a href={`https://wa.me/91${a.guest_phone}?text=Hi ${a.guest_name}, thanks for taking our mock test! You scored ${a.percentage}%. Join Signature PSC for full course.`} target="_blank" rel="noopener noreferrer"
                                className="text-emerald-400 hover:underline" data-testid={`whatsapp-${a.id}`}>{a.guest_phone}</a>
                            </td>
                            <td className="p-3 text-sm text-[#d4af37] font-bold">{a.score} / {a.total}</td>
                            <td className={`p-3 text-sm font-bold ${a.percentage >= 60 ? 'text-emerald-400' : a.percentage >= 33 ? 'text-amber-400' : 'text-red-400'}`}>{a.percentage}%</td>
                            <td className="p-3 text-xs">
                              <a href={`https://wa.me/91${a.guest_phone}?text=Hi ${a.guest_name}, thanks for taking Signature PSC mock test (scored ${a.percentage}%). Would you like to join our coaching?`} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20">
                                <Send className="w-3 h-3" /> Follow up
                              </a>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            )}
          </motion.div>
        )}

        {/* REPORTS TAB */}
        {tab === 'reports' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Report Type Selector */}
            <div className="flex gap-3 mb-4 flex-wrap">
              {['monthly', 'annual', 'daily_attendance', 'monthly_attendance', 'waived_fees'].map(rt => (
                <button key={rt} onClick={() => { setReportType(rt); if (rt === 'waived_fees') fetchWaivedFees(); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${reportType === rt ? 'bg-[#d4af37] text-[#18181b]' : 'bg-[#18181b] text-[#a1a1aa] border border-[#27272a] hover:bg-[#27272a]'}`}
                  data-testid={`report-type-${rt}`}>
                  {rt === 'monthly' ? 'Monthly Report' : rt === 'annual' ? 'Annual Report' : rt === 'daily_attendance' ? 'Daily Attendance' : rt === 'monthly_attendance' ? 'Monthly Attendance' : 'Waived Fees'}
                </button>
              ))}
            </div>

            {/* Monthly Report Controls */}
            {reportType === 'monthly' && (
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Month</Label>
                  <Select value={reportMonth} onValueChange={setReportMonth}>
                    <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white w-40" data-testid="report-month"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">{MONTHS.map(m => (<SelectItem key={m} value={m} className="text-white hover:bg-[#27272a]">{m}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Year</Label>
                  <Select value={String(reportYear)} onValueChange={v => setReportYear(parseInt(v))}>
                    <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white w-28" data-testid="report-year"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">{[2024,2025,2026,2027].map(y => (<SelectItem key={y} value={String(y)} className="text-white hover:bg-[#27272a]">{y}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchReport} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="generate-report-btn">
                  <FileText className="w-4 h-4 mr-2" /> Generate
                </Button>
                {report && <Button onClick={downloadReportPDF} className="bg-[#047857] text-white hover:bg-[#059669] font-semibold" data-testid="download-report-btn"><Download className="w-4 h-4 mr-2" /> Download PDF</Button>}
              </div>
            )}

            {/* Annual Report Controls */}
            {reportType === 'annual' && (
              <div className="flex gap-4 items-end">
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Year</Label>
                  <Select value={String(reportYear)} onValueChange={v => setReportYear(parseInt(v))}>
                    <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white w-28"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">{[2024,2025,2026,2027].map(y => (<SelectItem key={y} value={String(y)} className="text-white hover:bg-[#27272a]">{y}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchAnnualReport} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="annual-report-btn"><FileText className="w-4 h-4 mr-2" /> Generate Annual</Button>
                {annualReport && <Button onClick={downloadAnnualPDF} className="bg-[#047857] text-white hover:bg-[#059669] font-semibold" data-testid="download-annual-btn"><Download className="w-4 h-4 mr-2" /> Download PDF</Button>}
              </div>
            )}

            {/* Daily Attendance Controls */}
            {reportType === 'daily_attendance' && (
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Date</Label>
                  <Input type="date" value={dailyDate} onChange={e => setDailyDate(e.target.value)}
                    className="bg-[#18181b] border-[#27272a] text-white focus:border-[#d4af37] w-44" data-testid="daily-date" />
                </div>
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Batch (Optional)</Label>
                  <Select value={dailyBatch} onValueChange={setDailyBatch}>
                    <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white w-44"><SelectValue placeholder="All batches" /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">
                      <SelectItem value="all" className="text-white hover:bg-[#27272a]">All Batches</SelectItem>
                      {batches.map(b => (<SelectItem key={b.id} value={b.id} className="text-white hover:bg-[#27272a]">{b.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchDailyAttendance} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="daily-attendance-btn"><Calendar className="w-4 h-4 mr-2" /> Check Attendance</Button>
              </div>
            )}

            {/* Monthly Attendance Controls */}
            {reportType === 'monthly_attendance' && (
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Month</Label>
                  <Select value={reportMonth} onValueChange={setReportMonth}>
                    <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white w-40" data-testid="monthly-att-month"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">{MONTHS.map(mn => (<SelectItem key={mn} value={mn} className="text-white hover:bg-[#27272a]">{mn}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Year</Label>
                  <Select value={String(reportYear)} onValueChange={v => setReportYear(parseInt(v))}>
                    <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white w-28" data-testid="monthly-att-year"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">{[2024,2025,2026,2027].map(yr => (<SelectItem key={yr} value={String(yr)} className="text-white hover:bg-[#27272a]">{yr}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#a1a1aa] text-xs uppercase mb-1 block">Batch (Optional)</Label>
                  <Select value={monthlyAttBatch} onValueChange={setMonthlyAttBatch}>
                    <SelectTrigger className="bg-[#18181b] border-[#27272a] text-white w-44"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">
                      <SelectItem value="all" className="text-white hover:bg-[#27272a]">All Batches</SelectItem>
                      {batches.map(b => (<SelectItem key={b.id} value={b.id} className="text-white hover:bg-[#27272a]">{b.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={fetchMonthlyAttendance} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="monthly-attendance-btn"><Calendar className="w-4 h-4 mr-2" /> Generate</Button>
                {monthlyAttendance && <Button onClick={downloadMonthlyAttendancePDF} className="bg-[#047857] text-white hover:bg-[#059669] font-semibold" data-testid="download-monthly-att-btn"><Download className="w-4 h-4 mr-2" /> Download PDF</Button>}
              </div>
            )}

            {/* Monthly Report Data */}
            {reportType === 'monthly' && report && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Monthly Fee Collected</p><p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.income.monthly_collected}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Admission Fee</p><p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.income.admission_collected}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Other Income</p><p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.income.other_income || 0}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-emerald-500/30"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Total Income</p><p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.income.total_income}</p></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Monthly Fee Pending</p><p className="text-2xl font-bold text-red-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.income.monthly_pending}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Expenses</p><p className="text-2xl font-bold text-[#f59e0b]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.expenses.total}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-emerald-500/30"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Net Profit</p><p className={`text-2xl font-bold ${report.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.net_profit}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#d4af37]/30"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Balance</p><p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{report.profit_withdrawals?.balance || 0}</p></div>
                </div>
                {/* Attendance */}
                <div className="p-6 rounded-lg bg-[#18181b] border border-[#27272a]">
                  <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Attendance Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center"><p className="text-xl font-bold text-emerald-400">{report.attendance.present}</p><p className="text-xs text-[#a1a1aa]">Present</p></div>
                    <div className="text-center"><p className="text-xl font-bold text-amber-400">{report.attendance.late}</p><p className="text-xs text-[#a1a1aa]">Late</p></div>
                    <div className="text-center"><p className="text-xl font-bold text-violet-400">{report.attendance.leave}</p><p className="text-xs text-[#a1a1aa]">Leave</p></div>
                    <div className="text-center"><p className="text-xl font-bold text-red-400">{report.attendance.absent}</p><p className="text-xs text-[#a1a1aa]">Absent</p></div>
                    <div className="text-center"><p className="text-xl font-bold text-white">{report.attendance.total}</p><p className="text-xs text-[#a1a1aa]">Total</p></div>
                  </div>
                </div>
                {/* Expense breakdown */}
                {Object.keys(report.expenses.by_category || {}).length > 0 && (
                  <div className="p-6 rounded-lg bg-[#18181b] border border-[#27272a]">
                    <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Expense Breakdown</h3>
                    {Object.entries(report.expenses.by_category).map(([cat, amt]) => (
                      <div key={cat} className="flex justify-between py-2 border-b border-[#27272a] last:border-0">
                        <span className="text-sm text-[#a1a1aa] capitalize">{cat.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-bold text-[#f59e0b]">₹{amt}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Other Income breakdown */}
                {report.other_incomes?.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-emerald-500/20 overflow-hidden" data-testid="report-other-income-table">
                    <div className="p-4 border-b border-[#27272a] bg-emerald-500/5"><h3 className="text-base font-semibold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>Other Income — {report.other_incomes.length} entries</h3></div>
                    <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#27272a]">
                      {['Date', 'Source', 'Description', 'Method', 'Amount'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                    </tr></thead><tbody>
                      {report.other_incomes.map((i, idx) => (
                        <tr key={idx} className="border-b border-[#27272a] last:border-0">
                          <td className="p-3 text-sm text-[#a1a1aa]">{i.date}</td>
                          <td className="p-3 text-sm text-[#d4af37] capitalize">{i.category}</td>
                          <td className="p-3 text-sm text-[#a1a1aa]">{i.description || '-'}</td>
                          <td className="p-3 text-sm text-[#a1a1aa] capitalize">{i.payment_method}</td>
                          <td className="p-3 text-sm text-emerald-400 font-bold">₹{i.amount}</td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  </div>
                )}
                {/* Pending Fees with Student Names */}
                {report.pending_fees?.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-red-500/20 overflow-hidden">
                    <div className="p-4 border-b border-[#27272a] bg-red-500/5"><h3 className="text-base font-semibold text-red-400" style={{ fontFamily: "'Playfair Display', serif" }}>Pending Fees — {report.pending_fees.length} students</h3></div>
                    <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#27272a]">
                      {['Student Name', 'ID', 'Phone', 'Batch', 'Amount', 'Type'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                    </tr></thead><tbody>
                      {report.pending_fees.map((f, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-3 text-sm text-white font-medium">{f.student_name}</td>
                          <td className="p-3 text-sm text-[#d4af37] font-mono text-xs">{f.student_id}</td>
                          <td className="p-3 text-sm text-[#a1a1aa]">{f.phone}</td>
                          <td className="p-3 text-sm text-[#a1a1aa] capitalize">{f.batch}</td>
                          <td className="p-3 text-sm text-red-400 font-bold">₹{f.amount}</td>
                          <td className="p-3 text-sm text-[#a1a1aa] capitalize">{f.type}</td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  </div>
                )}
                {/* Paid Fees */}
                {report.paid_fees?.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-emerald-500/20 overflow-hidden">
                    <div className="p-4 border-b border-[#27272a] bg-emerald-500/5"><h3 className="text-base font-semibold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>Paid Fees — {report.paid_fees.length} students</h3></div>
                    <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#27272a]">
                      {['Student Name', 'Amount', 'Paid Date', 'Method'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                    </tr></thead><tbody>
                      {report.paid_fees.map((f, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0">
                          <td className="p-3 text-sm text-white">{f.student_name}</td>
                          <td className="p-3 text-sm text-emerald-400 font-bold">₹{f.amount}</td>
                          <td className="p-3 text-sm text-[#a1a1aa]">{f.paid_date?.split('T')[0] || '-'}</td>
                          <td className="p-3 text-sm text-[#a1a1aa] capitalize">{f.payment_method || '-'}</td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  </div>
                )}
              </div>
            )}

            {/* Annual Report Data */}
            {reportType === 'annual' && annualReport && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Fees Collected</p><p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{annualReport.income.total_collected}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Other Income</p><p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{annualReport.income.total_other_income || 0}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-emerald-500/30"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Total Income</p><p className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{annualReport.income.total_income || annualReport.income.total_collected}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Total Pending</p><p className="text-2xl font-bold text-red-400" style={{ fontFamily: "'Playfair Display', serif" }}>₹{annualReport.income.total_pending}</p></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a]"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Total Expenses</p><p className="text-2xl font-bold text-[#f59e0b]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{annualReport.expenses.total}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-emerald-500/30"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Net Profit</p><p className={`text-2xl font-bold ${annualReport.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} style={{ fontFamily: "'Playfair Display', serif" }}>₹{annualReport.net_profit}</p></div>
                  <div className="p-5 rounded-lg bg-[#18181b] border border-[#d4af37]/30"><p className="text-xs text-[#a1a1aa] uppercase mb-1">Balance</p><p className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: "'Playfair Display', serif" }}>₹{annualReport.balance}</p></div>
                </div>
                {/* Monthly Breakdown Table */}
                {annualReport.monthly_breakdown?.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden">
                    <div className="p-4 border-b border-[#27272a]"><h3 className="text-base font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Month-by-Month Breakdown</h3></div>
                    <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#27272a]">
                      {['Month', 'Fees', 'Other Inc.', 'Pending', 'Expenses', 'Net'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                    </tr></thead><tbody>
                      {annualReport.monthly_breakdown.map((m, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-3 text-sm text-white font-medium">{m.month}</td>
                          <td className="p-3 text-sm text-emerald-400 font-bold">₹{m.collected}</td>
                          <td className="p-3 text-sm text-emerald-300">₹{m.other_income || 0}</td>
                          <td className="p-3 text-sm text-red-400">₹{m.pending}</td>
                          <td className="p-3 text-sm text-[#f59e0b]">₹{m.expenses}</td>
                          <td className={`p-3 text-sm font-bold ${m.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>₹{m.net}</td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  </div>
                )}
                {/* Annual Pending Fees */}
                {annualReport.pending_fees?.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-red-500/20 overflow-hidden">
                    <div className="p-4 border-b border-[#27272a] bg-red-500/5"><h3 className="text-base font-semibold text-red-400">All Pending Fees — {annualReport.pending_fees.length} records</h3></div>
                    <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#27272a]">
                      {['Student', 'Month', 'Type', 'Amount', 'Phone', 'Batch'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                    </tr></thead><tbody>
                      {annualReport.pending_fees.map((f, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0">
                          <td className="p-3 text-sm text-white">{f.student_name}</td>
                          <td className="p-3 text-sm text-[#a1a1aa]">{f.month}</td>
                          <td className="p-3 text-sm text-[#a1a1aa] capitalize">{f.type}</td>
                          <td className="p-3 text-sm text-red-400 font-bold">₹{f.amount}</td>
                          <td className="p-3 text-sm text-[#a1a1aa]">{f.phone}</td>
                          <td className="p-3 text-sm text-[#a1a1aa] capitalize">{f.batch}</td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  </div>
                )}
                {/* Attendance */}
                <div className="p-6 rounded-lg bg-[#18181b] border border-[#27272a]">
                  <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Annual Attendance</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center"><p className="text-xl font-bold text-emerald-400">{annualReport.attendance.present}</p><p className="text-xs text-[#a1a1aa]">Present</p></div>
                    <div className="text-center"><p className="text-xl font-bold text-amber-400">{annualReport.attendance.late}</p><p className="text-xs text-[#a1a1aa]">Late</p></div>
                    <div className="text-center"><p className="text-xl font-bold text-violet-400">{annualReport.attendance.leave}</p><p className="text-xs text-[#a1a1aa]">Leave</p></div>
                    <div className="text-center"><p className="text-xl font-bold text-red-400">{annualReport.attendance.absent}</p><p className="text-xs text-[#a1a1aa]">Absent</p></div>
                  </div>
                </div>
              </div>
            )}

            {/* Daily Attendance Data */}
            {reportType === 'daily_attendance' && dailyAttendance && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a] text-center"><p className="text-xl font-bold text-white">{dailyAttendance.summary.total_students}</p><p className="text-xs text-[#a1a1aa]">Total Students</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-emerald-500/30 text-center"><p className="text-xl font-bold text-emerald-400">{dailyAttendance.summary.present}</p><p className="text-xs text-[#a1a1aa]">Present</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-amber-500/30 text-center"><p className="text-xl font-bold text-amber-400">{dailyAttendance.summary.late}</p><p className="text-xs text-[#a1a1aa]">Late</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-violet-500/30 text-center"><p className="text-xl font-bold text-violet-400">{dailyAttendance.summary.leave}</p><p className="text-xs text-[#a1a1aa]">Leave</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-red-500/30 text-center"><p className="text-xl font-bold text-red-400">{dailyAttendance.summary.absent_marked + dailyAttendance.summary.not_marked}</p><p className="text-xs text-[#a1a1aa]">Absent/Not Marked</p></div>
                </div>
                {/* Absent Student List */}
                <div className="rounded-lg bg-[#18181b] border border-red-500/20 overflow-hidden">
                  <div className="p-4 border-b border-[#27272a] bg-red-500/5"><h3 className="text-base font-semibold text-red-400">Absent / Not Marked — {dailyAttendance.absent_list?.length || 0} students</h3></div>
                  <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#27272a]">
                    {['Student Name', 'ID', 'Phone', 'Batch', 'Status'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                  </tr></thead><tbody>
                    {(dailyAttendance.absent_list?.length || 0) === 0 ? (
                      <tr><td colSpan={5} className="p-6 text-center text-emerald-400">All students are present!</td></tr>
                    ) : dailyAttendance.absent_list.map((s, i) => (
                      <tr key={i} className="border-b border-[#27272a] last:border-0">
                        <td className="p-3 text-sm text-white font-medium">{s.student_name}</td>
                        <td className="p-3 text-sm text-[#d4af37] font-mono text-xs">{s.student_id}</td>
                        <td className="p-3 text-sm text-[#a1a1aa]">{s.phone}</td>
                        <td className="p-3 text-sm text-[#a1a1aa] capitalize">{s.batch}</td>
                        <td className="p-3"><Badge className="bg-red-500/10 text-red-400 border-red-500/30">{s.status || 'not marked'}</Badge></td>
                      </tr>
                    ))}
                  </tbody></table></div>
                </div>
                {/* Present List */}
                {dailyAttendance.present_list?.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-emerald-500/20 overflow-hidden">
                    <div className="p-4 border-b border-[#27272a] bg-emerald-500/5"><h3 className="text-base font-semibold text-emerald-400">Present — {dailyAttendance.present_list.length} students</h3></div>
                    <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-[#27272a]">
                      {['Student Name', 'Phone', 'Batch'].map(h => (<th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>))}
                    </tr></thead><tbody>
                      {dailyAttendance.present_list.map((s, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0">
                          <td className="p-3 text-sm text-white">{s.student_name}</td>
                          <td className="p-3 text-sm text-[#a1a1aa]">{s.phone}</td>
                          <td className="p-3 text-sm text-[#a1a1aa] capitalize">{s.batch}</td>
                        </tr>
                      ))}
                    </tbody></table></div>
                  </div>
                )}
              </div>
            )}

            {/* Monthly Attendance Data */}
            {reportType === 'monthly_attendance' && monthlyAttendance && (
              <div className="space-y-6">
                {/* Totals */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="p-4 rounded-lg bg-[#18181b] border border-[#d4af37]/40 text-center" data-testid="class-days-held"><p className="text-xl font-bold text-[#d4af37]">{monthlyAttendance.class_days_held}</p><p className="text-xs text-[#a1a1aa]">Class Days Held</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-[#27272a] text-center"><p className="text-xl font-bold text-white">{monthlyAttendance.totals.students}</p><p className="text-xs text-[#a1a1aa]">Students</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-emerald-500/30 text-center"><p className="text-xl font-bold text-emerald-400">{monthlyAttendance.totals.present}</p><p className="text-xs text-[#a1a1aa]">Present</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-amber-500/30 text-center"><p className="text-xl font-bold text-amber-400">{monthlyAttendance.totals.late}</p><p className="text-xs text-[#a1a1aa]">Late</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-violet-500/30 text-center"><p className="text-xl font-bold text-violet-400">{monthlyAttendance.totals.leave}</p><p className="text-xs text-[#a1a1aa]">Leave</p></div>
                  <div className="p-4 rounded-lg bg-[#18181b] border border-red-500/30 text-center"><p className="text-xl font-bold text-red-400">{monthlyAttendance.totals.absent}</p><p className="text-xs text-[#a1a1aa]">Absent</p></div>
                </div>

                {/* Day-by-Day Class Totals */}
                {monthlyAttendance.day_totals?.length > 0 && (
                  <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="day-totals-table">
                    <div className="p-4 border-b border-[#27272a]">
                      <h3 className="text-base font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Class-Day Totals — {monthlyAttendance.day_totals.length} classes
                      </h3>
                      <p className="text-xs text-[#52525b] mt-1">How many students per day in each status</p>
                    </div>
                    <div className="overflow-x-auto"><table className="w-full">
                      <thead><tr className="border-b border-[#27272a]">
                        {['Date', 'Day', 'Present', 'Late', 'Leave', 'Absent', 'Total Marked'].map(h => (
                          <th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase">{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {monthlyAttendance.day_totals.map((d, i) => {
                          const dayName = new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short' });
                          return (
                            <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                              <td className="p-3 text-sm text-white font-medium">{d.date}</td>
                              <td className="p-3 text-sm text-[#a1a1aa]">{dayName}</td>
                              <td className="p-3 text-sm text-emerald-400 font-bold">{d.present}</td>
                              <td className="p-3 text-sm text-amber-400">{d.late}</td>
                              <td className="p-3 text-sm text-violet-400">{d.leave}</td>
                              <td className="p-3 text-sm text-red-400">{d.absent}</td>
                              <td className="p-3 text-sm text-[#d4af37] font-bold">{d.total_marked}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table></div>
                  </div>
                )}

                {/* Per-Student Summary Table */}
                <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="monthly-att-table"><div className="p-4 border-b border-[#27272a] flex justify-between items-center">
                    <h3 className="text-base font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Each Student — {monthlyAttendance.month} {monthlyAttendance.year} ({monthlyAttendance.class_days_held} classes held / {monthlyAttendance.days_in_month} days)
                    </h3>
                    <span className="text-xs text-[#52525b]">Click a row to see day-by-day</span>
                  </div>
                  <div className="overflow-x-auto"><table className="w-full">
                    <thead><tr className="border-b border-[#27272a]">
                      {['Student', 'Batch', 'Present', 'Late', 'Leave', 'Absent', 'Attended / Classes', 'Att %', 'Tests', 'Avg Marks', 'Best %'].map(h => (
                        <th key={h} className="text-left p-3 text-xs text-[#a1a1aa] uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {monthlyAttendance.students.length === 0 ? (
                        <tr><td colSpan={11} className="p-6 text-center text-[#52525b]">No active students</td></tr>
                      ) : monthlyAttendance.students.map((s, i) => (
                        <React.Fragment key={i}>
                          <tr
                            onClick={() => setExpandedStudent(expandedStudent === s.student_id ? null : s.student_id)}
                            className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30 cursor-pointer"
                            data-testid={`student-att-row-${s.student_id}`}>
                            <td className="p-3 text-sm text-white font-medium">{s.student_name} <span className="text-[10px] text-[#52525b] ml-1 font-mono">{s.student_id}</span></td>
                            <td className="p-3 text-sm text-[#a1a1aa] capitalize">{s.batch_id}</td>
                            <td className="p-3 text-sm text-emerald-400 font-bold">{s.present}</td>
                            <td className="p-3 text-sm text-amber-400">{s.late}</td>
                            <td className="p-3 text-sm text-violet-400">{s.leave}</td>
                            <td className="p-3 text-sm text-red-400">{s.absent}</td>
                            <td className="p-3 text-sm text-[#a1a1aa]">{s.present + s.late}/{s.class_days_held}</td>
                            <td className={`p-3 text-sm font-bold ${s.percentage >= 75 ? 'text-emerald-400' : s.percentage >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{s.percentage}%</td>
                            <td className="p-3 text-sm text-white">{s.tests_taken || 0}</td>
                            <td className="p-3 text-sm text-[#d4af37] font-bold">{s.total_marks || 0}{s.total_possible ? `/${s.total_possible}` : ''}</td>
                            <td className={`p-3 text-sm font-bold ${(s.best_test_pct || 0) >= 60 ? 'text-emerald-400' : (s.best_test_pct || 0) >= 33 ? 'text-amber-400' : 'text-[#52525b]'}`}>{s.best_test_pct || 0}%</td>
                          </tr>
                          {expandedStudent === s.student_id && (
                            <tr><td colSpan={11} className="bg-[#0f0f12] p-4">
                              <div className="grid grid-cols-7 sm:grid-cols-10 md:grid-cols-14 lg:grid-cols-15 gap-1.5">
                                {s.days.map((d, idx) => {
                                  const cls = d.status === 'present' ? 'bg-emerald-500/80 text-white' :
                                              d.status === 'late' ? 'bg-amber-500/80 text-white' :
                                              d.status === 'leave' ? 'bg-violet-500/80 text-white' :
                                              d.status === 'absent' ? 'bg-red-500/80 text-white' :
                                              'bg-[#27272a] text-[#52525b]';
                                  const code = d.status === 'present' ? 'P' : d.status === 'late' ? 'L' : d.status === 'leave' ? 'Le' : d.status === 'absent' ? 'A' : '-';
                                  return (
                                    <div key={idx} className={`${cls} rounded-md px-2 py-2 text-center`} title={`${d.date}: ${d.status || 'not marked'}`}>
                                      <div className="text-[10px] font-bold">{d.day}</div>
                                      <div className="text-[10px]">{code}</div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="flex flex-wrap gap-3 mt-3 text-[10px]">
                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/80"></span> Present</span>
                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/80"></span> Late</span>
                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-violet-500/80"></span> Leave</span>
                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/80"></span> Absent</span>
                                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#27272a]"></span> Not Marked</span>
                              </div>
                            </td></tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table></div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ENQUIRIES */}
        {tab === 'enquiries' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-end">
              <Button onClick={() => setShowEnquiry(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="admin-add-enquiry-btn">
                <MessageSquare className="w-4 h-4 mr-2" /> Add Enquiry
              </Button>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden">
              <div className="p-6 border-b border-[#27272a]"><h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>All Enquiries</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-[#27272a]">
                    {['Name', 'Phone', 'Course', 'Source', 'Notes', 'Status', 'Date'].map(h => (<th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {enquiries.length === 0 ? (<tr><td colSpan={7} className="p-8 text-center text-[#52525b]">No enquiries</td></tr>) : (
                      enquiries.map((eq, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{eq.name}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{eq.phone}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{eq.course_interest || '-'}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] capitalize">{eq.source}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] max-w-[200px] truncate">{eq.notes || '-'}</td>
                          <td className="p-4"><Badge className={eq.status === 'new' ? 'bg-[#d4af37]/10 text-[#d4af37] border-[#d4af37]/30' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'}>{eq.status}</Badge></td>
                          <td className="p-4 text-sm text-[#52525b]">{eq.created_at?.split('T')[0]}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* CONTACTS / WEBSITE ENQUIRIES */}
        {tab === 'contacts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="contacts-table">
              <div className="p-6 border-b border-[#27272a]">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Contact Enquiries</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Name', 'Email', 'Phone', 'Message', 'Date'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.length === 0 ? (
                      <tr><td colSpan={5} className="p-8 text-center text-[#52525b]">No enquiries yet</td></tr>
                    ) : (
                      contacts.map((c, i) => (
                        <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                          <td className="p-4 text-sm text-white">{c.name}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{c.email}</td>
                          <td className="p-4 text-sm text-[#a1a1aa]">{c.phone}</td>
                          <td className="p-4 text-sm text-[#a1a1aa] max-w-xs truncate">{c.message}</td>
                          <td className="p-4 text-sm text-[#52525b]">{c.created_at?.split('T')[0]}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* USERS/STAFF */}
        {tab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <div className="flex justify-end">
              <Button onClick={() => setShowCreateUser(true)} className="bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="create-user-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Create Staff Account
              </Button>
            </div>
            <div className="rounded-lg bg-[#18181b] border border-[#27272a] overflow-hidden" data-testid="users-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272a]">
                      {['Name', 'Email', 'Role', 'Actions'].map(h => (
                        <th key={h} className="text-left p-4 text-xs text-[#a1a1aa] uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.role !== 'student').map((u, i) => (
                      <tr key={i} className="border-b border-[#27272a] last:border-0 hover:bg-[#27272a]/30">
                        <td className="p-4 text-sm text-white">{u.name}</td>
                        <td className="p-4 text-sm text-[#a1a1aa]">{u.email}</td>
                        <td className="p-4"><Badge className={u.role === 'admin' ? 'bg-[#d4af37]/10 text-[#d4af37] border-[#d4af37]/30' : 'bg-[#047857]/10 text-emerald-400 border-emerald-500/30'}>{u.role}</Badge></td>
                        <td className="p-4">
                          {u.role !== 'admin' && (
                            <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id)} className="text-red-400 hover:bg-red-500/10" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Register Dialog */}
      <Dialog open={showRegister} onOpenChange={setShowRegister}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-register-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>New Student Admission</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-[#a1a1aa] text-xs">Full Name *</Label>
                <Input value={regForm.name} onChange={e => setRegForm({ ...regForm, name: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="admin-reg-name" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Date of Birth *</Label>
                <Input type="date" value={regForm.dob} onChange={e => setRegForm({ ...regForm, dob: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="admin-reg-dob" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Phone *</Label>
                <Input value={regForm.phone} onChange={e => setRegForm({ ...regForm, phone: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="admin-reg-phone" />
              </div>
              <div className="col-span-2">
                <Label className="text-[#a1a1aa] text-xs">Email (Optional)</Label>
                <Input type="email" value={regForm.email} onChange={e => setRegForm({ ...regForm, email: e.target.value })} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" />
              </div>
              <div className="col-span-2">
                <Label className="text-[#a1a1aa] text-xs">Address *</Label>
                <Input value={regForm.address} onChange={e => setRegForm({ ...regForm, address: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="admin-reg-address" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Marital Status</Label>
                <Select value={regForm.marital_status} onValueChange={v => setRegForm({ ...regForm, marital_status: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    <SelectItem value="single" className="text-white hover:bg-[#27272a]">Single</SelectItem>
                    <SelectItem value="married" className="text-white hover:bg-[#27272a]">Married</SelectItem>
                    <SelectItem value="other" className="text-white hover:bg-[#27272a]">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Education *</Label>
                <Select value={regForm.educational_qualification} onValueChange={v => setRegForm({ ...regForm, educational_qualification: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {EDUCATION_OPTIONS.map(opt => (<SelectItem key={opt} value={opt} className="text-white hover:bg-[#27272a]">{opt}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Batch *</Label>
                <Select value={regForm.batch_id} onValueChange={v => setRegForm({ ...regForm, batch_id: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {batches.filter(b => b.id !== 'online').map(b => (<SelectItem key={b.id} value={b.id} className="text-white hover:bg-[#27272a]">{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Branch (Location) *</Label>
                <Select value={regForm.location} onValueChange={v => setRegForm({ ...regForm, location: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="admin-reg-location"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {locations.active.map(l => (<SelectItem key={l.slug} value={l.slug} className="text-white hover:bg-[#27272a]">{l.name} ({l.district})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Monthly Fee *</Label>
                <Select value={String(regForm.monthly_fee)} onValueChange={v => setRegForm({ ...regForm, monthly_fee: parseInt(v) })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="admin-reg-monthly-fee"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    <SelectItem value="800" className="text-white hover:bg-[#27272a]">₹800 / month</SelectItem>
                    <SelectItem value="1000" className="text-white hover:bg-[#27272a]">₹1000 / month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Admission Fee</Label>
                <Select value={String(regForm.admission_fee)} onValueChange={v => setRegForm({ ...regForm, admission_fee: parseInt(v) })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="admin-reg-admission-fee"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    <SelectItem value="0" className="text-white hover:bg-[#27272a]">₹0 (No Admission Fee)</SelectItem>
                    <SelectItem value="1000" className="text-white hover:bg-[#27272a]">₹1000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Waiver — single month only. Future months must be waived individually from Fees tab. */}
              <div className="md:col-span-2 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-white" data-testid="admin-reg-waive-admission-wrap">
                  <input type="checkbox" checked={regForm.admission_fee === 0}
                    onChange={e => setRegForm({ ...regForm, admission_fee: e.target.checked ? 0 : 1000 })}
                    className="w-4 h-4 accent-purple-500" data-testid="admin-reg-waive-admission" />
                  <span className="font-medium">⊗ Waive Admission Fee</span>
                  <span className="text-[10px] text-[#a1a1aa]">(sets admission to ₹0)</span>
                </label>
                <p className="text-[10px] text-[#a1a1aa] mt-2">
                  💡 To waive a <strong>specific month's fee</strong>, go to <strong>Fees tab → Collect → Waive Off</strong>. Each month must be waived individually.
                </p>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Username (Login ID)</Label>
                <Input value={regForm.username} onChange={e => setRegForm({ ...regForm, username: e.target.value })}
                  placeholder="Phone or custom username" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="admin-reg-username" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Password</Label>
                <Input value={regForm.password} onChange={e => setRegForm({ ...regForm, password: e.target.value })}
                  placeholder="Simple password" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="admin-reg-password" />
              </div>
            </div>
            <p className="text-xs text-[#52525b]">If left empty, phone = username, last 4 digits = password.</p>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="admin-reg-submit">
              Register Student
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Create Staff Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label className="text-[#a1a1aa] text-xs">Name *</Label>
              <Input value={userForm.name} onChange={e => setUserForm({ ...userForm, name: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="user-name" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Email *</Label>
              <Input type="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="user-email" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Password *</Label>
              <Input type="password" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="user-password" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Role</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  <SelectItem value="staff" className="text-white hover:bg-[#27272a]">Staff</SelectItem>
                  <SelectItem value="admin" className="text-white hover:bg-[#27272a]">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="user-submit">Create Account</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Success Story Dialog */}
      <Dialog open={showAddStory} onOpenChange={setShowAddStory}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="add-story-dialog">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Add Success Story</DialogTitle></DialogHeader>
          <form onSubmit={handleAddStory} className="space-y-4">
            <div><Label className="text-[#a1a1aa] text-xs">Student Name *</Label><Input value={storyForm.student_name} onChange={e => setStoryForm({ ...storyForm, student_name: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="story-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-[#a1a1aa] text-xs">Exam *</Label><Input value={storyForm.exam} onChange={e => setStoryForm({ ...storyForm, exam: e.target.value })} required placeholder="e.g. Kerala PSC" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="story-exam" /></div>
              <div><Label className="text-[#a1a1aa] text-xs">Year *</Label><Input value={storyForm.year} onChange={e => setStoryForm({ ...storyForm, year: e.target.value })} required placeholder="e.g. 2025" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="story-year" /></div>
            </div>
            <div><Label className="text-[#a1a1aa] text-xs">Post / Position *</Label><Input value={storyForm.post} onChange={e => setStoryForm({ ...storyForm, post: e.target.value })} required placeholder="e.g. LDC, Police Constable" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="story-post" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Photo (Upload)</Label>
              <Input type="file" accept="image/*" onChange={e => setStoryPhotoFile(e.target.files[0])}
                className="bg-[#09090b] border-[#27272a] text-white file:bg-[#d4af37] file:text-[#18181b] file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:font-semibold file:text-xs" data-testid="story-photo" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Details</Label><Textarea value={storyForm.details} onChange={e => setStoryForm({ ...storyForm, details: e.target.value })} rows={2} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37] resize-none" data-testid="story-details" /></div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="story-submit">Add Success Story</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Teacher Dialog */}
      <Dialog open={showAddTeacher} onOpenChange={setShowAddTeacher}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="add-teacher-dialog">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Add Teacher</DialogTitle></DialogHeader>
          <form onSubmit={handleAddTeacher} className="space-y-4">
            <div><Label className="text-[#a1a1aa] text-xs">Name *</Label><Input value={teacherForm.name} onChange={e => setTeacherForm({ ...teacherForm, name: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="teacher-name" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Specialization *</Label><Input value={teacherForm.specialization} onChange={e => setTeacherForm({ ...teacherForm, specialization: e.target.value })} required placeholder="e.g. General Studies, Maths" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="teacher-spec" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Experience *</Label><Input value={teacherForm.experience} onChange={e => setTeacherForm({ ...teacherForm, experience: e.target.value })} required placeholder="e.g. 8 Years" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="teacher-exp" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Photo (Upload)</Label>
              <Input type="file" accept="image/*" onChange={e => setTeacherPhotoFile(e.target.files[0])}
                className="bg-[#09090b] border-[#27272a] text-white file:bg-[#d4af37] file:text-[#18181b] file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:font-semibold file:text-xs" data-testid="teacher-photo" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Details</Label><Textarea value={teacherForm.details} onChange={e => setTeacherForm({ ...teacherForm, details: e.target.value })} rows={2} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37] resize-none" data-testid="teacher-details" /></div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="teacher-submit">Add Teacher</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Withdraw Profit Dialog */}
      <Dialog open={showWithdrawProfit} onOpenChange={setShowWithdrawProfit}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="withdraw-profit-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Record Profit Withdrawal</DialogTitle>
          </DialogHeader>
          {profitSummary && (
            <div className="p-3 rounded-md bg-[#09090b] border border-[#d4af37]/30 mb-3">
              <p className="text-xs text-[#a1a1aa]">Available Net Profit: <span className={`font-bold ${profitSummary.balance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>₹{profitSummary.balance}</span></p>
            </div>
          )}
          <form onSubmit={handleWithdrawProfit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Month</Label>
                <Select value={profitForm.month} onValueChange={v => setProfitForm({ ...profitForm, month: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {MONTHS.map(m => (<SelectItem key={m} value={m} className="text-white hover:bg-[#27272a]">{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Year</Label>
                <Select value={String(profitForm.year)} onValueChange={v => setProfitForm({ ...profitForm, year: parseInt(v) })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {[2024,2025,2026,2027].map(y => (<SelectItem key={y} value={String(y)} className="text-white hover:bg-[#27272a]">{y}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Date</Label>
              <Input type="date" value={profitForm.date} onChange={e => setProfitForm({ ...profitForm, date: e.target.value })} required
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="profit-date" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Partner 1 Name</Label>
                <Input value={profitForm.partner1_name} onChange={e => setProfitForm({ ...profitForm, partner1_name: e.target.value })}
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="partner1-name" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Partner 1 Amount (₹)</Label>
                <Input type="number" value={profitForm.partner1_amount} onChange={e => setProfitForm({ ...profitForm, partner1_amount: e.target.value })} required min="0"
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="partner1-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Partner 2 Name</Label>
                <Input value={profitForm.partner2_name} onChange={e => setProfitForm({ ...profitForm, partner2_name: e.target.value })}
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="partner2-name" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Partner 2 Amount (₹)</Label>
                <Input type="number" value={profitForm.partner2_amount} onChange={e => setProfitForm({ ...profitForm, partner2_amount: e.target.value })} required min="0"
                  className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="partner2-amount" />
              </div>
            </div>
            <Button type="button" onClick={splitEqually} variant="outline" className="w-full border-[#3f3f46] text-[#a1a1aa] hover:bg-[#27272a] text-sm" data-testid="split-equally-btn">
              Split Net Profit Equally (₹{profitSummary ? Math.floor(profitSummary.net_profit / 2) : 0} each)
            </Button>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Notes</Label>
              <Input value={profitForm.notes} onChange={e => setProfitForm({ ...profitForm, notes: e.target.value })}
                placeholder="e.g. Monthly profit sharing" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Payment Method *</Label>
              <Select value={profitForm.payment_method} onValueChange={v => setProfitForm({ ...profitForm, payment_method: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="withdraw-method"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  <SelectItem value="cash" className="text-white hover:bg-[#27272a]">Cash</SelectItem>
                  <SelectItem value="upi" className="text-white hover:bg-[#27272a]">UPI</SelectItem>
                  <SelectItem value="bank_transfer" className="text-white hover:bg-[#27272a]">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#52525b] mt-1">This will be deducted from the corresponding Cash / UPI / Bank balance.</p>
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="profit-submit">
              Record Withdrawal — ₹{(parseFloat(profitForm.partner1_amount) || 0) + (parseFloat(profitForm.partner2_amount) || 0)}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="add-expense-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Add Expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddExpense} className="space-y-4">
            <div>
              <Label className="text-[#a1a1aa] text-xs">Category *</Label>
              <Select value={expenseForm.category} onValueChange={v => setExpenseForm({ ...expenseForm, category: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  {EXPENSE_CATEGORIES.map(c => (<SelectItem key={c.id} value={c.id} className="text-white hover:bg-[#27272a]">{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Amount (₹) *</Label>
              <Input type="number" value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} required min="1"
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="expense-amount" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Date *</Label>
              <Input type="date" value={expenseForm.date} onChange={e => setExpenseForm({ ...expenseForm, date: e.target.value })} required
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="expense-date" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Description</Label>
              <Input value={expenseForm.description} onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="e.g. Monthly rent payment" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="expense-description" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Payment Method</Label>
              <Select value={expenseForm.payment_method} onValueChange={v => setExpenseForm({ ...expenseForm, payment_method: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  <SelectItem value="cash" className="text-white hover:bg-[#27272a]">Cash</SelectItem>
                  <SelectItem value="upi" className="text-white hover:bg-[#27272a]">UPI</SelectItem>
                  <SelectItem value="bank_transfer" className="text-white hover:bg-[#27272a]">Bank Transfer</SelectItem>
                  <SelectItem value="cheque" className="text-white hover:bg-[#27272a]">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="expense-submit">Add Expense</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Other Income Dialog */}
      <Dialog open={showAddIncome} onOpenChange={setShowAddIncome}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="add-income-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Add Other Income</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddIncome} className="space-y-4">
            <div>
              <Label className="text-[#a1a1aa] text-xs">Source *</Label>
              <Select value={incomeForm.category} onValueChange={v => setIncomeForm({ ...incomeForm, category: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="income-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  {INCOME_CATEGORIES.map(c => (<SelectItem key={c.id} value={c.id} className="text-white hover:bg-[#27272a]">{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Amount (₹) *</Label>
              <Input type="number" value={incomeForm.amount} onChange={e => setIncomeForm({ ...incomeForm, amount: e.target.value })} required min="1"
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="income-amount" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Date *</Label>
              <Input type="date" value={incomeForm.date} onChange={e => setIncomeForm({ ...incomeForm, date: e.target.value })} required
                className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="income-date" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Description</Label>
              <Input value={incomeForm.description} onChange={e => setIncomeForm({ ...incomeForm, description: e.target.value })}
                placeholder="e.g. Personal contribution" className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" data-testid="income-description" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Payment Method</Label>
              <Select value={incomeForm.payment_method} onValueChange={v => setIncomeForm({ ...incomeForm, payment_method: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  <SelectItem value="cash" className="text-white hover:bg-[#27272a]">Cash</SelectItem>
                  <SelectItem value="upi" className="text-white hover:bg-[#27272a]">UPI</SelectItem>
                  <SelectItem value="bank_transfer" className="text-white hover:bg-[#27272a]">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="income-submit">Add Income</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pay Fee Dialog */}
      <Dialog open={showPayFee} onOpenChange={setShowPayFee}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="admin-pay-fee-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Record Fee Action</DialogTitle>
          </DialogHeader>
          <div className="mb-4">
            <p className="text-sm text-[#a1a1aa]">Student: <span className="text-[#d4af37] font-mono">{selectedStudent}</span></p>
            <div className="mt-3 space-y-2">
              {fees.filter(f => f.student_id === selectedStudent && ['pending','partial'].includes(f.status)).map((f, i) => {
                const balance = Math.max(0, (f.amount || 0) - (f.amount_paid || 0));
                return (
                  <button key={i} type="button" onClick={() => { setPayForm({ ...payForm, month: f.month, year: f.year, amount: f.amount }); setPartialAmount(balance); }}
                    className={`w-full text-left p-3 rounded-md border transition-all ${payForm.month === f.month && payForm.year === f.year ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-[#27272a] bg-[#09090b]'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white capitalize">{f.month} {f.year} ({f.type}) {f.status === 'partial' && <span className="text-blue-300 text-xs ml-1">· partial</span>}</span>
                      <span className="text-sm font-bold text-[#d4af37]">₹{f.amount}</span>
                    </div>
                    {(f.amount_paid > 0) && <p className="text-[10px] text-emerald-400 mt-0.5">Paid ₹{f.amount_paid} · Balance ₹{balance}</p>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode Tabs */}
          <div className="flex gap-1 p-1 rounded-lg bg-[#09090b] border border-[#27272a] mb-3" data-testid="pay-mode-tabs">
            {[['full','Full'], ['partial','Partial'], ['waive','Waive Off']].map(([m, lbl]) => (
              <button key={m} type="button" onClick={() => setPayMode(m)}
                className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${payMode === m ? 'bg-[#d4af37] text-[#18181b]' : 'text-[#a1a1aa] hover:bg-[#27272a]'}`}
                data-testid={`pay-mode-${m}`}>
                {lbl}
              </button>
            ))}
          </div>

          <form onSubmit={handlePayFee} className="space-y-3">
            {payMode !== 'waive' && (
              <>
                {payMode === 'partial' && (
                  <div>
                    <Label className="text-[#a1a1aa] text-xs">Amount to Collect Now (₹)</Label>
                    <Input type="number" min="1" value={partialAmount} onChange={e => setPartialAmount(e.target.value)}
                      className="bg-[#09090b] border-[#27272a] text-white" data-testid="pay-partial-amount" required />
                  </div>
                )}
                <div>
                  <Label className="text-[#a1a1aa] text-xs">Payment Method</Label>
                  <Select value={payForm.payment_method} onValueChange={v => setPayForm({ ...payForm, payment_method: v })}>
                    <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#27272a]">
                      <SelectItem value="cash" className="text-white hover:bg-[#27272a]">Cash</SelectItem>
                      <SelectItem value="upi" className="text-white hover:bg-[#27272a]">UPI</SelectItem>
                      <SelectItem value="bank_transfer" className="text-white hover:bg-[#27272a]">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {payMode === 'waive' && (
              <div>
                <Label className="text-[#a1a1aa] text-xs">Reason for Waiver</Label>
                <Input value={waiveReason} onChange={e => setWaiveReason(e.target.value)} placeholder="e.g. Scholarship, financial hardship, staff family..."
                  className="bg-[#09090b] border-[#27272a] text-white" data-testid="pay-waive-reason" />
                <p className="text-[10px] text-purple-300 mt-1">This will mark the fee as fully waived — no collection recorded.</p>
              </div>
            )}
            <Button type="submit" className={`w-full font-semibold ${payMode === 'waive' ? 'bg-purple-500 text-white hover:bg-purple-600' : 'bg-[#d4af37] text-[#18181b] hover:bg-[#facc15]'}`} data-testid="pay-submit-btn">
              {payMode === 'waive' ? 'Waive Off Fee' : payMode === 'partial' ? `Collect Partial ₹${partialAmount || 0}` : `Collect Full ₹${payForm.amount}`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={showCredentials} onOpenChange={setShowCredentials}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="admin-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Student Login Credentials</DialogTitle>
          </DialogHeader>
          {credentials && (
            <div className="space-y-4">
              <div className="p-4 rounded-md bg-[#09090b] border border-[#d4af37]/30">
                <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-2">Student Login Credentials</p>
                <div className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm text-[#a1a1aa]">Student ID</span><span className="text-sm text-[#d4af37] font-mono">{credentials.student_id}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-[#a1a1aa]">Name</span><span className="text-sm text-white">{credentials.name}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-[#a1a1aa]">Username</span><span className="text-sm text-white font-mono">{credentials.username}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-[#a1a1aa]">Password</span><span className="text-sm text-white font-mono">{credentials.password}</span></div>
                </div>
              </div>
              <Button onClick={() => { navigator.clipboard.writeText(`Username: ${credentials.username}\nPassword: ${credentials.password}`); toast.success('Copied!'); }}
                className="w-full bg-[#27272a] text-white hover:bg-[#3f3f46]"><Copy className="w-4 h-4 mr-2" /> Copy Credentials</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Enquiry Dialog */}
      <Dialog open={showEnquiry} onOpenChange={setShowEnquiry}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="admin-enquiry-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Add Enquiry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddEnquiry} className="space-y-4">
            <div><Label className="text-[#a1a1aa] text-xs">Name *</Label><Input value={enquiryForm.name} onChange={e => setEnquiryForm({ ...enquiryForm, name: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Phone *</Label><Input value={enquiryForm.phone} onChange={e => setEnquiryForm({ ...enquiryForm, phone: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Email</Label><Input value={enquiryForm.email} onChange={e => setEnquiryForm({ ...enquiryForm, email: e.target.value })} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Course Interest</Label><Input value={enquiryForm.course_interest} onChange={e => setEnquiryForm({ ...enquiryForm, course_interest: e.target.value })} placeholder="LDC, LGS..." className="bg-[#09090b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#d4af37]" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Source</Label>
              <Select value={enquiryForm.source} onValueChange={v => setEnquiryForm({ ...enquiryForm, source: v })}>
                <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#27272a]">
                  {['walk-in', 'phone', 'referral', 'website', 'social_media'].map(s => (<SelectItem key={s} value={s} className="text-white hover:bg-[#27272a] capitalize">{s.replace('_', ' ')}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[#a1a1aa] text-xs">Notes</Label><Textarea value={enquiryForm.notes} onChange={e => setEnquiryForm({ ...enquiryForm, notes: e.target.value })} rows={3} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37] resize-none" /></div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold">Add Enquiry</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp Reminder Dialog */}
      <Dialog open={showBulkReminder} onOpenChange={setShowBulkReminder}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-bulk-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp Fee Reminders
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#a1a1aa] mb-4">Click on a student to open WhatsApp with a pre-filled fee reminder message.</p>
          {bulkReminders.length === 0 ? (
            <p className="text-center text-[#52525b] py-6">No pending fees to remind</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              <p className="text-xs text-[#d4af37] font-medium">{bulkReminders.length} pending reminders</p>
              {bulkReminders.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 rounded-md bg-[#09090b] border border-[#27272a] hover:border-[#25D366]/50 transition-all group cursor-pointer">
                  <div className="flex-1">
                    <p className="text-sm text-white font-medium">{r.name}</p>
                    <p className="text-xs text-[#a1a1aa]">{r.phone} — {r.month} {r.year} ({r.type})</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#d4af37]">₹{r.amount}</span>
                    <div className="w-8 h-8 rounded-full bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366]/20 transition-colors">
                      <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Fee Receipt</DialogTitle></DialogHeader>
          {receipt && (<>
            <div ref={receiptRef} className="bg-white text-black p-6 rounded-md">
              <div className="text-center border-b-2 border-[#d4af37] pb-3 mb-4"><h1 className="text-lg font-bold text-black">{receipt.centre_name}</h1><p className="text-xs text-gray-600">{receipt.centre_address} | {receipt.centre_phone}</p></div>
              <p className="text-right text-xs text-gray-500 mb-3">Receipt: {receipt.receipt_no}</p>
              <table className="w-full text-sm"><tbody>
                {[['Student', receipt.student_name], ['ID', receipt.student_id], ['Month', `${receipt.month} ${receipt.year}`], ['Type', receipt.type], ['Method', receipt.payment_method], ['Date', receipt.paid_date?.split('T')[0]]].map(([k, v]) => (
                  <tr key={k} className="border-b border-gray-200"><td className="py-2 font-semibold text-gray-700">{k}</td><td className="py-2 text-right text-black capitalize">{v}</td></tr>
                ))}
              </tbody></table>
              <div className="text-center py-4 mt-3 bg-gray-50 border border-[#d4af37] rounded"><span className="text-2xl font-bold text-black">₹{receipt.amount}</span></div>
            </div>
            <Button onClick={handleDownloadPDF} className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold mt-3"><Printer className="w-4 h-4 mr-2" /> Download PDF Receipt</Button>
          </>)}
        </DialogContent>
      </Dialog>
      {/* Edit Student Dialog */}
      <Dialog open={showEditStudent} onOpenChange={setShowEditStudent}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="admin-edit-student-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Edit Student</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditStudent} className="space-y-3">
            <div><Label className="text-[#a1a1aa] text-xs">Name</Label><Input value={editStudentForm.name} onChange={e => setEditStudentForm({ ...editStudentForm, name: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="edit-student-name" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Phone</Label><Input value={editStudentForm.phone} onChange={e => setEditStudentForm({ ...editStudentForm, phone: e.target.value })} required className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="edit-student-phone" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Email</Label><Input value={editStudentForm.email} onChange={e => setEditStudentForm({ ...editStudentForm, email: e.target.value })} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" /></div>
            <div><Label className="text-[#a1a1aa] text-xs">Address</Label><Textarea value={editStudentForm.address} onChange={e => setEditStudentForm({ ...editStudentForm, address: e.target.value })} rows={2} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37] resize-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Batch</Label>
                <Select value={editStudentForm.batch_id} onValueChange={v => setEditStudentForm({ ...editStudentForm, batch_id: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="edit-student-batch"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {batches.map(b => (<SelectItem key={b.id} value={b.id} className="text-white hover:bg-[#27272a]">{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Branch</Label>
                <Select value={editStudentForm.location} onValueChange={v => setEditStudentForm({ ...editStudentForm, location: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {(locations.active || []).map(l => (<SelectItem key={l.slug} value={l.slug} className="text-white hover:bg-[#27272a]">{l.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Monthly Fee (₹)</Label>
                <Input type="number" value={editStudentForm.monthly_fee} onChange={e => setEditStudentForm({ ...editStudentForm, monthly_fee: e.target.value })} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="edit-student-fee" />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Qualification</Label>
                <Select value={editStudentForm.educational_qualification} onValueChange={v => setEditStudentForm({ ...editStudentForm, educational_qualification: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {EDUCATION_OPTIONS.map(e => (<SelectItem key={e} value={e} className="text-white hover:bg-[#27272a]">{e}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="edit-student-submit">Save Changes</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Fee Dialog */}
      <Dialog open={showEditFee} onOpenChange={setShowEditFee}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="admin-edit-fee-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Edit Fee Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditFee} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Amount (₹)</Label>
                <Input type="number" step="0.01" value={editFeeForm.amount} onChange={e => setEditFeeForm({ ...editFeeForm, amount: e.target.value })} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="edit-fee-amount" required />
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Amount Paid (₹)</Label>
                <Input type="number" step="0.01" value={editFeeForm.amount_paid} onChange={e => setEditFeeForm({ ...editFeeForm, amount_paid: e.target.value })} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Month</Label>
                <Select value={editFeeForm.month} onValueChange={v => setEditFeeForm({ ...editFeeForm, month: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {['admission', ...MONTHS].map(m => (<SelectItem key={m} value={m} className="text-white hover:bg-[#27272a] capitalize">{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Year</Label>
                <Input type="number" value={editFeeForm.year} onChange={e => setEditFeeForm({ ...editFeeForm, year: e.target.value })} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#a1a1aa] text-xs">Type</Label>
                <Select value={editFeeForm.type} onValueChange={v => setEditFeeForm({ ...editFeeForm, type: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {['monthly', 'admission', 'other'].map(t => (<SelectItem key={t} value={t} className="text-white hover:bg-[#27272a] capitalize">{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#a1a1aa] text-xs">Status</Label>
                <Select value={editFeeForm.status} onValueChange={v => setEditFeeForm({ ...editFeeForm, status: v })}>
                  <SelectTrigger className="bg-[#09090b] border-[#27272a] text-white" data-testid="edit-fee-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#27272a]">
                    {['pending', 'partial', 'paid', 'waived'].map(s => (<SelectItem key={s} value={s} className="text-white hover:bg-[#27272a] capitalize">{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="edit-fee-submit">Save Changes</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Batch Create/Edit Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="bg-[#18181b] border-[#27272a] text-white max-w-md" data-testid="admin-batch-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{editingBatchId ? 'Edit Batch' : 'New Batch'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBatch} className="space-y-3">
            {!editingBatchId && (
              <div>
                <Label className="text-[#a1a1aa] text-xs">Batch ID (lowercase, no spaces)</Label>
                <Input value={batchForm.id} onChange={e => setBatchForm({ ...batchForm, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })} required placeholder="e.g. morning" className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="batch-id-input" />
              </div>
            )}
            <div>
              <Label className="text-[#a1a1aa] text-xs">Batch Name</Label>
              <Input value={batchForm.name} onChange={e => setBatchForm({ ...batchForm, name: e.target.value })} required placeholder="e.g. Morning Batch" className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="batch-name-input" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Timing</Label>
              <Input value={batchForm.timing} onChange={e => setBatchForm({ ...batchForm, timing: e.target.value })} placeholder="e.g. 8:00 AM - 10:00 AM" className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37]" data-testid="batch-timing-input" />
            </div>
            <div>
              <Label className="text-[#a1a1aa] text-xs">Description</Label>
              <Textarea value={batchForm.description} onChange={e => setBatchForm({ ...batchForm, description: e.target.value })} rows={2} className="bg-[#09090b] border-[#27272a] text-white focus:border-[#d4af37] resize-none" />
            </div>
            <Button type="submit" className="w-full bg-[#d4af37] text-[#18181b] hover:bg-[#facc15] font-semibold" data-testid="batch-submit">
              {editingBatchId ? 'Save Changes' : 'Create Batch'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OverviewCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-5 rounded-lg bg-[#18181b] border border-[#27272a] hover:border-[#d4af37]/20 transition-all" data-testid={`overview-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">{label}</p>
          <p className="text-2xl font-bold" style={{ color: accent, fontFamily: "'Playfair Display', serif" }}>{value}</p>
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}
